import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect, MultiSearchableSelect } from '../components/ui/searchable-select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { 
  Plus, MoreHorizontal, Edit, Trash2, Loader2, Building2, GraduationCap, 
  MapPin, Calendar, FileText, ClipboardList, X, Target, 
  CircleDot, Zap, Bell, Search, ChevronRight, Settings2, Database,
  AlertCircle, Download, HardDrive, Archive, RefreshCw, Mail, Tags, Globe,
  Ban, AlertTriangle, Sparkles, Workflow, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import CSVImportModal from '../components/CSVImportModal';
import CSVExportButton from '../components/CSVExportButton';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import QuickCoursesManager from '../components/QuickCoursesManager';
import PublicSiteSettingsPanel from '../components/settings/PublicSiteSettingsPanel';

// Settings categories for sidebar navigation
const settingsCategories = [
  {
    id: 'academic-hierarchy',
    label: 'Academic Hierarchy',
    icon: GraduationCap,
    items: [
      { key: 'academic_hierarchy', label: 'Hierarchy Manager', icon: Building2, description: 'University → Campus → Degree → Program', isCustom: true },
    ]
  },
  {
    id: 'master-data',
    label: 'Master Data',
    icon: Database,
    items: [
      { key: 'university', label: 'Universities', icon: Building2, description: 'Partner universities' },
      { key: 'campus', label: 'Campuses', icon: Building2, description: 'University campuses' },
      { key: 'faculty', label: 'Faculties', icon: GraduationCap, description: 'University faculties/departments' },
      { key: 'subject', label: 'Subjects', icon: GraduationCap, description: 'Academic subjects/disciplines' },
      { key: 'degree_type', label: 'Degree Types', icon: GraduationCap, description: 'Degree levels' },
      { key: 'program', label: 'Programs', icon: GraduationCap, description: 'Courses and programs offered' },
      { key: 'country', label: 'Countries', icon: MapPin, description: 'Supported countries' },
      { key: 'city', label: 'Cities', icon: MapPin, description: 'Cities within countries' },
      { key: 'intake', label: 'Intakes', icon: Calendar, description: 'Intake periods' },
      { key: 'document_type', label: 'Document Types', icon: FileText, description: 'Required document types' },
      { key: 'application_status', label: 'App Statuses', icon: CircleDot, description: 'Workflow statuses' },
      { key: 'application_type', label: 'Application Types', icon: Tags, description: 'Source types (Direct, Agent, Web Form)' },
    ]
  },
  {
    id: 'university-config',
    label: 'University Config',
    icon: Building2,
    items: [
      { key: 'required_docs', label: 'Required Docs', icon: ClipboardList, description: 'Documents per university', isCustom: true },
      { key: 'eligibility_criteria', label: 'Eligibility Criteria', icon: Target, description: 'Country & Program admission criteria', isCustom: true },
      { key: 'quick_courses', label: 'Quick Courses Searcher', icon: Search, description: 'Import & manage course catalogue', isCustom: true },
    ]
  },
  {
    id: 'automation',
    label: 'Automation',
    icon: Zap,
    items: [
      { key: 'automation', label: 'Automation Rules', icon: Zap, description: 'Email & SMS automation', isCustom: true },
    ]
  },
  {
    id: 'agent-settings',
    label: 'Agent Settings',
    icon: Settings2,
    items: [
      { key: 'agent_permissions', label: 'Agent Permissions', icon: Settings2, description: 'Configure what agents can access', isCustom: true },
    ]
  },
  {
    id: 'public-site',
    label: 'Public Site',
    icon: Globe,
    items: [
      { key: 'company_info', label: 'Company Info', icon: Globe, description: 'Address, phones, emails & social links shown on the public website', isCustom: true },
    ]
  },
  {
    id: 'data-backup',
    label: 'Data & Backup',
    icon: Archive,
    items: [
      { key: 'data_backup', label: 'Data Backup', icon: Download, description: 'Download your organization data', isCustom: true },
    ]
  }
];

// Flatten all items for search
const allSettingsItems = settingsCategories.flatMap(cat => 
  cat.items.map(item => ({ ...item, category: cat.label }))
);

const CoreSettingsPage = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('academic_hierarchy');
  const [searchQuery, setSearchQuery] = useState('');
  // r60 — Per-table free-text search. Filters by name, parent, subject, and
  // faculty so users can quickly find a program by any visible column.
  const [tableSearch, setTableSearch] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [parentOptions, setParentOptions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [formData, setFormData] = useState({ name: '', parent_id: '', metadata: {} });

  // Logo upload state for the Universities tab
  const [pendingLogoFile, setPendingLogoFile] = useState(null);   // file chosen but not yet uploaded
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreviewBlob, setLogoPreviewBlob] = useState(null);   // local object-URL preview for `pendingLogoFile`

  // AI enrichment state for Add Program dialog (Sparkles button)
  const [aiEnriching, setAiEnriching] = useState(false);

  // Custom tab states
  const [universities, setUniversities] = useState([]);
  const [requiredDocsConfigs, setRequiredDocsConfigs] = useState([]);
  const [customLoading, setCustomLoading] = useState(true);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newDocName, setNewDocName] = useState('');
  const [currentDocs, setCurrentDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [automationSettings, setAutomationSettings] = useState(null);
  
  // Country-based required docs states
  const [selectedDocCountry, setSelectedDocCountry] = useState('');
  const [countryDocsList, setCountryDocsList] = useState([]);
  const [addCountryDialogOpen, setAddCountryDialogOpen] = useState(false);
  const [newCountryForDocs, setNewCountryForDocs] = useState('');
  
  // Degree type based required docs states (NEW)
  const [selectedDegreeType, setSelectedDegreeType] = useState('');
  const [degreeTypeDocs, setDegreeTypeDocs] = useState({});  // { degreeType: [docs] }
  const [degreeTypes, setDegreeTypes] = useState([]);
  
  // Eligibility criteria states
  const [eligibilityDialogOpen, setEligibilityDialogOpen] = useState(false);
  const [eligibilityCriteria, setEligibilityCriteria] = useState({});
  const [universityCriteriaData, setUniversityCriteriaData] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countryCriteriaList, setCountryCriteriaList] = useState([]);
  const [newCriteriaText, setNewCriteriaText] = useState('');
  const [closedRegions, setClosedRegions] = useState([]); // array of {type,name} objects (back-compat: also accepts plain strings)
  const [newClosedRegion, setNewClosedRegion] = useState('');
  const [newClosedRegionType, setNewClosedRegionType] = useState('custom'); // city|province|district|country|custom
  
  // Program Specific Criteria states (within country context)
  const [selectedProgramForCriteria, setSelectedProgramForCriteria] = useState(null);
  const [programCriteriaList, setProgramCriteriaList] = useState([]);
  const [newProgramCriteriaText, setNewProgramCriteriaText] = useState('');
  const [programsList, setProgramsList] = useState([]);
  const [countryProgramCriteria, setCountryProgramCriteria] = useState([]); // All program criteria for current country
  
  const [criteriaFormData, setCriteriaFormData] = useState({
    degree_type: '',
    min_percentage: '',
    min_gpa: '',
    english_test: '',
    min_english_score: '',
    additional_requirements: ''
  });
  
  // Academic hierarchy states
  const [hierarchyUniversities, setHierarchyUniversities] = useState([]);
  const [hierarchyCampuses, setHierarchyCampuses] = useState([]);
  const [hierarchyDegreeTypes, setHierarchyDegreeTypes] = useState([]);
  const [hierarchyPrograms, setHierarchyPrograms] = useState([]);
  const [selectedHierarchyUniversity, setSelectedHierarchyUniversity] = useState('');
  const [selectedHierarchyCampus, setSelectedHierarchyCampus] = useState('');
  const [selectedHierarchyDegreeType, setSelectedHierarchyDegreeType] = useState('');
  const [hierarchyFormData, setHierarchyFormData] = useState({
    university_name: '', campus_name: '', degree_type: '', program_name: '', subject: '', faculty: '', duration: '', fees: ''
  });
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [hierarchyCreateOpen, setHierarchyCreateOpen] = useState(false);
  const [hierarchyCreateType, setHierarchyCreateType] = useState(''); // university, campus, degree_type, program
  
  // Agent permissions state
  const [agentPermissions, setAgentPermissions] = useState({
    view_own_applications: true,
    view_application_status: true,
    upload_documents: false,
    edit_applications: false,
    view_communications: false,
    view_documents: false,
    notify_on_status_change: true,
    notify_on_desk_shift: true
  });
  const [agentPermissionsLoading, setAgentPermissionsLoading] = useState(false);
  const [savingAgentPermissions, setSavingAgentPermissions] = useState(false);

  // Program dialog conditional dropdown states
  const [programDialogCampuses, setProgramDialogCampuses] = useState([]);
  const [programDialogSubjects, setProgramDialogSubjects] = useState([]);
  const [programDialogFaculties, setProgramDialogFaculties] = useState([]);
  const [programDialogDegreeTypes, setProgramDialogDegreeTypes] = useState([]);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddType, setQuickAddType] = useState(''); // campus, subject, faculty
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddLoading, setQuickAddLoading] = useState(false);

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return settingsCategories;
    const query = searchQuery.toLowerCase();
    return settingsCategories.map(cat => ({
      ...cat,
      items: cat.items.filter(item => 
        item.label.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query)
      )
    })).filter(cat => cat.items.length > 0);
  }, [searchQuery]);

  const getActiveItemConfig = useCallback(() => {
    return allSettingsItems.find(item => item.key === activeTab);
  }, [activeTab]);

  const activeConfig = getActiveItemConfig();
  const needsParent = activeTab === 'city' || activeTab === 'program' || activeTab === 'campus' || activeTab === 'degree_type' || activeTab === 'subject' || activeTab === 'faculty';

  // Fetch main data
  useEffect(() => {
    const fetchData = async () => {
      if (activeConfig?.isCustom) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await api().get(`/master-data?type=${activeTab}`);
        let items = response.data;
        
        // Resolve parent names for items with parent_id
        if (items.length > 0 && items.some(item => item.parent_id)) {
          const parentsMap = {};
          if (activeTab === 'city') {
            const parentsRes = await api().get(`/master-data?type=country`);
            parentsRes.data.forEach(p => { parentsMap[p.id] = p.name; });
          } else if (activeTab === 'program') {
            // Programs can sit under a degree_type (most common) or a university directly.
            // Pull both so the Parent column resolves correctly instead of showing "-".
            const [dtRes, uniRes] = await Promise.all([
              api().get(`/master-data?type=degree_type`),
              api().get(`/master-data?type=university`),
            ]);
            dtRes.data.forEach(p => { parentsMap[p.id] = p.name; });
            uniRes.data.forEach(p => { parentsMap[p.id] = p.name; });
          } else {
            const parentsRes = await api().get(`/master-data?type=university`);
            parentsRes.data.forEach(p => { parentsMap[p.id] = p.name; });
          }
          items = items.map(item => ({
            ...item,
            parent_name: item.parent_id ? parentsMap[item.parent_id] || '-' : '-'
          }));
        }
        
        setData(items);
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    
    if (activeTab === 'city') {
      api().get('/master-data?type=country').then(res => setParentOptions(res.data)).catch(() => {});
    } else if (activeTab === 'program' || activeTab === 'degree_type') {
      api().get('/master-data?type=university').then(res => setParentOptions(res.data)).catch(() => {});
    } else if (activeTab === 'campus' || activeTab === 'subject' || activeTab === 'faculty') {
      api().get('/master-data?type=university').then(res => setParentOptions(res.data)).catch(() => {});
    }
  }, [activeTab, api, activeConfig?.isCustom]);

  // Fetch custom tab data
  useEffect(() => {
    const fetchCustomData = async () => {
      if (activeTab === 'required_docs') {
        setCustomLoading(true);
        try {
          const [uniRes, docsRes, countriesRes, degreeTypesRes] = await Promise.all([
            api().get('/master-data?type=university'),
            api().get('/university-required-docs'),
            api().get('/master-data?type=country'),
            api().get('/academic-hierarchy/degree-types')
          ]);
          setUniversities(uniRes.data);
          setRequiredDocsConfigs(docsRes.data);
          setCountries(countriesRes.data);
          setDegreeTypes(degreeTypesRes.data || []);
        } catch (error) {
          toast.error('Failed to load data');
        } finally {
          setCustomLoading(false);
        }
      } else if (activeTab === 'eligibility_criteria') {
        setCustomLoading(true);
        try {
          const [uniRes, countriesRes, criteriaRes] = await Promise.all([
            api().get('/master-data?type=university'),
            api().get('/master-data?type=country'),
            api().get('/university-eligibility-criteria')
          ]);
          setUniversities(uniRes.data);
          setCountries(countriesRes.data);
          // Store criteria configs by university
          const criteriaMap = {};
          criteriaRes.data.forEach(c => {
            criteriaMap[c.university_id] = c;
          });
          setEligibilityCriteria(criteriaMap);
        } catch (error) {
          toast.error('Failed to load eligibility data');
        } finally {
          setCustomLoading(false);
        }
      } else if (activeTab === 'automation') {
        setCustomLoading(true);
        try {
          const res = await api().get('/automation-settings');
          setAutomationSettings(res.data);
        } catch (error) {
          console.error('Failed to load settings');
        } finally {
          setCustomLoading(false);
        }
      } else if (activeTab === 'academic_hierarchy') {
        setCustomLoading(true);
        try {
          const [uniRes, campusRes, dtRes, progRes] = await Promise.all([
            api().get('/academic-hierarchy/universities'),
            api().get('/academic-hierarchy/campuses'),
            api().get('/academic-hierarchy/degree-types'),
            api().get('/academic-hierarchy/programs')
          ]);
          setHierarchyUniversities(uniRes.data);
          setHierarchyCampuses(campusRes.data);
          setHierarchyDegreeTypes(dtRes.data);
          setHierarchyPrograms(progRes.data);
        } catch (error) {
          console.error('Failed to load academic hierarchy');
        } finally {
          setCustomLoading(false);
        }
      }
    };
    fetchCustomData();
  }, [activeTab, api]);

  // Fetch campuses/subjects/faculties/degree-types when university changes in program dialog
  const fetchProgramDialogOptions = async (universityId) => {
    if (!universityId || !api) {
      setProgramDialogCampuses([]);
      setProgramDialogSubjects([]);
      setProgramDialogFaculties([]);
      setProgramDialogDegreeTypes([]);
      return;
    }
    try {
      const [campusRes, subjectRes, facultyRes, degreeTypeRes] = await Promise.all([
        api().get(`/master-data?type=campus`),
        api().get(`/master-data?type=subject`),
        api().get(`/master-data?type=faculty`),
        api().get(`/academic-hierarchy/degree-types?university_id=${universityId}`)
      ]);
      // Campuses are tied to a specific university (each campus is a physical
      // location), so they stay scoped. Subjects and faculties are conceptual
      // taxonomies the team curates once and reuses across universities, so
      // expose the entire tenant pool — sorted alphabetically for the
      // searchable dropdown. Degree types come from the academic-hierarchy
      // endpoint which already deduplicates by name and is tenant-scoped.
      const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');
      setProgramDialogCampuses(campusRes.data.filter(c => c.parent_id === universityId).sort(sortByName));
      setProgramDialogSubjects([...subjectRes.data].sort(sortByName));
      setProgramDialogFaculties([...facultyRes.data].sort(sortByName));
      setProgramDialogDegreeTypes([...(degreeTypeRes.data || [])].sort(sortByName));
    } catch (error) {
      console.error('Failed to fetch program dialog options');
    }
  };

  // Quick add for program dialog
  const handleQuickAdd = async () => {
    // For program tab, the form's parent_id holds the degree_type_id. The
    // campus quick-add must be parented to the *university* (held in
    // metadata.university_id), so resolve the right parent here.
    const isProgramFlow = activeTab === 'program';
    const universityIdForProgram = formData.metadata?.university_id || '';
    const parentForQuickAdd = (isProgramFlow && quickAddType === 'campus')
      ? universityIdForProgram
      : (isProgramFlow ? universityIdForProgram : formData.parent_id);

    // Subject / Faculty / Degree Type are tenant-wide taxonomies — quick add
    // them as standalone records (no parent) so they show up in the dropdowns
    // immediately for any university.
    const standaloneTypes = ['subject', 'faculty', 'degree_type'];
    const finalParentId = standaloneTypes.includes(quickAddType) ? null : parentForQuickAdd;

    if (!quickAddName.trim() || !quickAddType) {
      toast.error('Please enter a name');
      return;
    }
    if (!standaloneTypes.includes(quickAddType) && !finalParentId) {
      toast.error('Please select a university first');
      return;
    }
    setQuickAddLoading(true);
    try {
      const res = await api().post('/master-data', {
        type: quickAddType,
        name: quickAddName.trim(),
        parent_id: finalParentId,
        metadata: {}
      });
      toast.success(`${quickAddType.charAt(0).toUpperCase() + quickAddType.slice(1).replace('_', ' ')} created`);
      setQuickAddOpen(false);
      setQuickAddName('');
      // Refresh options
      if (isProgramFlow) {
        await fetchProgramDialogOptions(universityIdForProgram);
      }
      // Auto-select the new item
      const newId = res.data.id;
      if (quickAddType === 'campus') {
        setFormData(prev => ({ ...prev, metadata: { ...prev.metadata, campus: newId, campus_name: quickAddName.trim() } }));
      } else if (quickAddType === 'subject') {
        setFormData(prev => ({ ...prev, metadata: { ...prev.metadata, subject: newId, subject_name: quickAddName.trim() } }));
      } else if (quickAddType === 'faculty') {
        setFormData(prev => ({ ...prev, metadata: { ...prev.metadata, faculty: newId, faculty_name: quickAddName.trim() } }));
      } else if (quickAddType === 'degree_type') {
        setFormData(prev => ({ ...prev, parent_id: newId, metadata: { ...prev.metadata, degree_type_name: quickAddName.trim() } }));
      }
    } catch (error) {
      toast.error('Failed to create ' + quickAddType.replace('_', ' '));
    } finally {
      setQuickAddLoading(false);
    }
  };

  const handleCreate = async () => {
    // r60 — Programs require a Degree Type as parent (backend-enforced).
    // Surface a clear validation message before hitting the API.
    if (activeTab === 'program') {
      if (!formData.metadata?.university_id) {
        toast.error('Please select a university');
        return;
      }
      if (!formData.parent_id) {
        toast.error('Please select a degree type for this program');
        return;
      }
    }
    try {
      const createRes = await api().post('/master-data', {
        type: activeTab,
        name: formData.name,
        parent_id: formData.parent_id || null,
        metadata: formData.metadata
      });

      // If a logo file was selected for a university, upload it now using
      // the freshly-created item id.
      if (activeTab === 'university' && pendingLogoFile && createRes.data?.id) {
        try {
          const fd = new FormData();
          fd.append('logo', pendingLogoFile);
          await api().post(`/master-data/${createRes.data.id}/logo`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (uploadErr) {
          toast.error('University was created but the logo upload failed. Edit the university to retry.');
        }
      }

      toast.success('Item created successfully');
      setCreateOpen(false);
      setFormData({ name: '', parent_id: '', metadata: {} });
      setPendingLogoFile(null);
      setLogoUploading(false);
      const response = await api().get(`/master-data?type=${activeTab}`);
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create item');
    }
  };

  const handleUpdate = async () => {
    try {
      await api().put(`/master-data/${selectedItem.id}`, {
        name: formData.name,
        parent_id: formData.parent_id || null,
        metadata: formData.metadata
      });

      // Upload any pending logo file (e.g. user picked a new image during edit)
      if (activeTab === 'university' && pendingLogoFile && selectedItem?.id) {
        try {
          const fd = new FormData();
          fd.append('logo', pendingLogoFile);
          await api().post(`/master-data/${selectedItem.id}/logo`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (uploadErr) {
          toast.error('University was updated but the logo upload failed.');
        }
      }

      toast.success('Item updated successfully');
      setEditOpen(false);
      setSelectedItem(null);
      setPendingLogoFile(null);
      setLogoUploading(false);
      const response = await api().get(`/master-data?type=${activeTab}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const handleClearLogo = async () => {
    if (!selectedItem?.id) {
      // Create flow — just drop the pending file + any URL
      setPendingLogoFile(null);
      setFormData(prev => ({ ...prev, metadata: { ...prev.metadata, logo_url: '' } }));
      return;
    }
    try {
      await api().delete(`/master-data/${selectedItem.id}/logo`);
      setPendingLogoFile(null);
      setFormData(prev => ({ ...prev, metadata: { ...prev.metadata, logo_url: '' } }));
      toast.success('Logo removed');
    } catch (err) {
      toast.error('Failed to remove logo');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api().delete(`/master-data/${id}`);
      toast.success('Item deleted');
      const response = await api().get(`/master-data?type=${activeTab}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    if (!window.confirm(`Delete ${selectedItems.length} items?`)) return;
    try {
      await Promise.all(selectedItems.map(id => api().delete(`/master-data/${id}`)));
      toast.success(`${selectedItems.length} items deleted`);
      setSelectedItems([]);
      const response = await api().get(`/master-data?type=${activeTab}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to delete some items');
    }
  };

  const toggleItemSelection = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedItems(selectedItems.length === data.length ? [] : data.map(item => item.id));
  };

  const openEdit = (item) => {
    setSelectedItem(item);
    setFormData({ name: item.name, parent_id: item.parent_id || '', metadata: item.metadata || {} });
    setPendingLogoFile(null);
    setLogoPreviewBlob(null);
    setEditOpen(true);
  };

  // Required docs handlers - Country-based
  const openDocEditDialog = (university) => {
    setSelectedUniversity(university);
    const config = requiredDocsConfigs.find(c => c.university_id === university.id);
    setCountryDocsList(config?.country_requirements || []);
    setSelectedDocCountry('');
    setCurrentDocs([]);
    setEditDialogOpen(true);
  };

  const selectCountryForDocs = (country) => {
    setSelectedDocCountry(country);
    const countryReq = countryDocsList.find(c => c.country === country);
    // Load degree type docs for this country
    const dtDocs = countryReq?.degree_type_docs || {};
    setDegreeTypeDocs(dtDocs);
    // Select first degree type if available, otherwise show legacy docs
    if (Object.keys(dtDocs).length > 0) {
      const firstDegree = Object.keys(dtDocs)[0];
      setSelectedDegreeType(firstDegree);
      setCurrentDocs(dtDocs[firstDegree] || []);
    } else {
      setSelectedDegreeType('');
      setCurrentDocs(countryReq?.required_documents || []);
    }
  };

  const selectDegreeTypeForDocs = (degreeType) => {
    setSelectedDegreeType(degreeType);
    setCurrentDocs(degreeTypeDocs[degreeType] || []);
  };

  const addDocument = () => {
    if (!newDocName.trim()) return;
    if (currentDocs.includes(newDocName.trim())) {
      toast.error('Document already exists');
      return;
    }
    setCurrentDocs([...currentDocs, newDocName.trim()]);
    setNewDocName('');
  };

  const removeDocument = (docName) => {
    setCurrentDocs(currentDocs.filter(d => d !== docName));
  };

  const saveCountryDocs = async () => {
    if (!selectedDocCountry) {
      toast.error('Please select a country');
      return;
    }
    if (!selectedDegreeType) {
      toast.error('Please select a degree type');
      return;
    }
    
    setSaving(true);
    try {
      await api().put(`/university-required-docs/${selectedUniversity.id}/country/${selectedDocCountry}`, {
        required_documents: currentDocs,
        degree_type: selectedDegreeType
      });
      
      // Update local state - update degree_type_docs
      const updatedDtDocs = { ...degreeTypeDocs, [selectedDegreeType]: currentDocs };
      setDegreeTypeDocs(updatedDtDocs);
      
      const updatedList = [...countryDocsList];
      const existingIdx = updatedList.findIndex(c => c.country === selectedDocCountry);
      if (existingIdx >= 0) {
        updatedList[existingIdx].degree_type_docs = updatedDtDocs;
      } else {
        updatedList.push({ country: selectedDocCountry, degree_type_docs: updatedDtDocs, required_documents: [] });
      }
      setCountryDocsList(updatedList);
      
      toast.success(`Documents saved for ${selectedDocCountry} - ${selectedDegreeType}`);
      
      // Refresh configs
      const docsRes = await api().get('/university-required-docs');
      setRequiredDocsConfigs(docsRes.data);
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addNewCountryConfig = () => {
    if (!newCountryForDocs) {
      toast.error('Please select a country');
      return;
    }
    if (countryDocsList.find(c => c.country === newCountryForDocs)) {
      toast.error('Country already configured');
      return;
    }
    setCountryDocsList([...countryDocsList, { country: newCountryForDocs, required_documents: [] }]);
    setSelectedDocCountry(newCountryForDocs);
    setCurrentDocs([]);
    setNewCountryForDocs('');
    setAddCountryDialogOpen(false);
  };

  const deleteCountryConfig = async (country) => {
    if (!confirm(`Remove document requirements for ${country}?`)) return;
    
    try {
      await api().delete(`/university-required-docs/${selectedUniversity.id}/country/${country}`);
      setCountryDocsList(countryDocsList.filter(c => c.country !== country));
      if (selectedDocCountry === country) {
        setSelectedDocCountry('');
        setCurrentDocs([]);
      }
      toast.success(`Removed requirements for ${country}`);
      
      const docsRes = await api().get('/university-required-docs');
      setRequiredDocsConfigs(docsRes.data);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getDocCount = (uniId) => {
    const config = requiredDocsConfigs.find(c => c.university_id === uniId);
    const countryReqs = config?.country_requirements || [];
    return countryReqs.length;
  };

  const getTotalDocsCount = (uniId) => {
    const config = requiredDocsConfigs.find(c => c.university_id === uniId);
    const countryReqs = config?.country_requirements || [];
    return countryReqs.reduce((sum, c) => sum + (c.required_documents?.length || 0), 0);
  };

  // Render required docs content - Country-based
  const renderRequiredDocsContent = () => {
    if (customLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Required Documents by University & Country</h3>
            <p className="text-sm text-muted-foreground">Configure which documents are required for each university, based on student's country</p>
          </div>
        </div>
        
        <div className="border rounded-lg divide-y">
          {universities.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No universities configured yet</p>
              <p className="text-sm">Add universities in Master Data first</p>
            </div>
          ) : (
            universities.map((uni) => (
              <div key={uni.id} className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{uni.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {getDocCount(uni.id)} {getDocCount(uni.id) === 1 ? 'country' : 'countries'} configured
                      {getTotalDocsCount(uni.id) > 0 && ` • ${getTotalDocsCount(uni.id)} total documents`}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => openDocEditDialog(uni)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Configure
                </Button>
              </div>
            ))
          )}
        </div>

        {/* Country-based Required Documents Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[85vh]">
            <DialogHeader>
              <DialogTitle>Required Documents</DialogTitle>
              <DialogDescription>{selectedUniversity?.name} - Configure documents by country</DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-5 gap-4 h-[500px]">
              {/* Countries sidebar */}
              <div className="col-span-2 border rounded-lg p-3 space-y-2 overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Countries</p>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-7 text-xs"
                    onClick={() => setAddCountryDialogOpen(true)}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
                
                {countryDocsList.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground text-sm">
                    No countries configured.<br/>Click "Add" to add a country.
                  </p>
                ) : (
                  countryDocsList.map((countryConfig) => {
                    const dtDocsCount = Object.keys(countryConfig.degree_type_docs || {}).length;
                    const legacyDocsCount = countryConfig.required_documents?.length || 0;
                    return (
                      <div 
                        key={countryConfig.country}
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedDocCountry === countryConfig.country 
                            ? 'bg-primary text-primary-foreground' 
                            : 'hover:bg-muted'
                        }`}
                        onClick={() => selectCountryForDocs(countryConfig.country)}
                      >
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm">{countryConfig.country}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge variant={selectedDocCountry === countryConfig.country ? "secondary" : "outline"} className="text-xs">
                            {dtDocsCount > 0 ? `${dtDocsCount} degrees` : `${legacyDocsCount} docs`}
                          </Badge>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-6 w-6"
                            onClick={(e) => { e.stopPropagation(); deleteCountryConfig(countryConfig.country); }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              
              {/* Documents editor */}
              <div className="col-span-3 border rounded-lg p-3 space-y-3">
                {!selectedDocCountry ? (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <div className="text-center">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Select a country to edit its required documents</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="font-medium">Documents for {selectedDocCountry}</p>
                      <Badge variant="outline">{currentDocs.length} documents</Badge>
                    </div>
                    
                    {/* Degree Type Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm">Degree Type</Label>
                      <Select value={selectedDegreeType} onValueChange={selectDegreeTypeForDocs}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select degree type" />
                        </SelectTrigger>
                        <SelectContent>
                          {degreeTypes.map(dt => (
                            <SelectItem key={dt.id} value={dt.name}>
                              {dt.name}
                              {degreeTypeDocs[dt.name]?.length > 0 && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  ({degreeTypeDocs[dt.name].length} docs)
                                </span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Each degree type can have its own required documents
                      </p>
                    </div>
                    
                    {selectedDegreeType && (
                      <>
                        <div className="flex gap-2">
                          <Input
                            placeholder="Add document name..."
                            value={newDocName}
                            onChange={(e) => setNewDocName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addDocument()}
                          />
                          <Button onClick={addDocument} size="icon">
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <ScrollArea className="h-[250px] pr-2">
                          {currentDocs.length === 0 ? (
                            <p className="text-center py-8 text-muted-foreground text-sm">
                              No documents added for {selectedDegreeType}
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {currentDocs.map((doc, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-sm">{doc}</span>
                                  </div>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeDocument(doc)}>
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </ScrollArea>
                      </>
                    )}
                    
                    {!selectedDegreeType && (
                      <div className="text-center py-8 text-muted-foreground">
                        <p className="text-sm">Select a degree type to configure its required documents</p>
                      </div>
                    )}
                    
                    <Button onClick={saveCountryDocs} disabled={saving || !selectedDegreeType} className="w-full">
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Save Documents for {selectedDocCountry} - {selectedDegreeType || 'Select Degree'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Country Dialog */}
        <Dialog open={addCountryDialogOpen} onOpenChange={setAddCountryDialogOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Add Country</DialogTitle>
              <DialogDescription>Select a country to configure its required documents</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Select value={newCountryForDocs} onValueChange={setNewCountryForDocs}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a country..." />
                </SelectTrigger>
                <SelectContent>
                  {countries
                    .filter(c => !countryDocsList.find(cd => cd.country === c.name))
                    .map((country) => (
                      <SelectItem key={country.id} value={country.name}>{country.name}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddCountryDialogOpen(false)}>Cancel</Button>
              <Button onClick={addNewCountryConfig}>Add Country</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // ============ ELIGIBILITY CRITERIA FUNCTIONS ============
  
  // Parse plain English criteria into structured format
  const parseEnglishCriteria = (text) => {
    const criteria = {
      name: text,
      field_type: 'text',
      operator: 'custom',
      value: text,
      description: text,
      original_text: text
    };
    
    const lowerText = text.toLowerCase();
    
    // Parse educational gap patterns
    if (lowerText.includes('gap') || lowerText.includes('break')) {
      const yearMatch = text.match(/(\d+)\s*year/i);
      const monthMatch = text.match(/(\d+)\s*month/i);
      if (yearMatch) {
        criteria.name = 'Educational Gap';
        criteria.field_type = 'date_gap_years';
        criteria.operator = lowerText.includes('not') || lowerText.includes('less') || lowerText.includes('under') ? 'max' : 'max';
        criteria.value = yearMatch[1];
        criteria.unit = 'years';
        criteria.description = `Educational gap should not exceed ${yearMatch[1]} years`;
      } else if (monthMatch) {
        criteria.name = 'Educational Gap';
        criteria.field_type = 'date_gap_months';
        criteria.operator = 'max';
        criteria.value = monthMatch[1];
        criteria.unit = 'months';
        criteria.description = `Educational gap should not exceed ${monthMatch[1]} months`;
      }
    }
    // Parse percentage patterns
    else if (lowerText.includes('percentage') || lowerText.includes('%') || lowerText.includes('marks')) {
      const numMatch = text.match(/(\d+(?:\.\d+)?)\s*%?/);
      if (numMatch) {
        criteria.name = 'Minimum Percentage';
        criteria.field_type = 'number';
        criteria.operator = lowerText.includes('at least') || lowerText.includes('minimum') || lowerText.includes('above') ? 'min' : 'min';
        criteria.value = numMatch[1];
        criteria.unit = '%';
        criteria.description = `Minimum ${numMatch[1]}% required`;
      }
    }
    // Parse GPA patterns
    else if (lowerText.includes('gpa') || lowerText.includes('cgpa')) {
      const numMatch = text.match(/(\d+(?:\.\d+)?)/);
      if (numMatch) {
        criteria.name = 'Minimum GPA';
        criteria.field_type = 'number';
        criteria.operator = 'min';
        criteria.value = numMatch[1];
        criteria.unit = 'GPA';
        criteria.description = `Minimum GPA of ${numMatch[1]} required`;
      }
    }
    // Parse IELTS patterns
    else if (lowerText.includes('ielts')) {
      const numMatch = text.match(/(\d+(?:\.\d+)?)/);
      if (numMatch) {
        criteria.name = 'IELTS Score';
        criteria.field_type = 'number';
        criteria.operator = 'min';
        criteria.value = numMatch[1];
        criteria.unit = 'band';
        criteria.description = `Minimum IELTS score of ${numMatch[1]} required`;
      }
    }
    // Parse TOEFL patterns
    else if (lowerText.includes('toefl')) {
      const numMatch = text.match(/(\d+)/);
      if (numMatch) {
        criteria.name = 'TOEFL Score';
        criteria.field_type = 'number';
        criteria.operator = 'min';
        criteria.value = numMatch[1];
        criteria.unit = 'score';
        criteria.description = `Minimum TOEFL score of ${numMatch[1]} required`;
      }
    }
    // Parse PTE patterns
    else if (lowerText.includes('pte')) {
      const numMatch = text.match(/(\d+)/);
      if (numMatch) {
        criteria.name = 'PTE Score';
        criteria.field_type = 'number';
        criteria.operator = 'min';
        criteria.value = numMatch[1];
        criteria.unit = 'score';
        criteria.description = `Minimum PTE score of ${numMatch[1]} required`;
      }
    }
    // Parse age patterns
    else if (lowerText.includes('age')) {
      const numMatch = text.match(/(\d+)/);
      if (numMatch) {
        criteria.name = 'Age Requirement';
        criteria.field_type = 'number';
        criteria.operator = lowerText.includes('above') || lowerText.includes('over') || lowerText.includes('at least') ? 'min' : 
                          lowerText.includes('below') || lowerText.includes('under') || lowerText.includes('not exceed') ? 'max' : 'equals';
        criteria.value = numMatch[1];
        criteria.unit = 'years';
        criteria.description = `Age ${criteria.operator === 'min' ? 'minimum' : criteria.operator === 'max' ? 'maximum' : ''} ${numMatch[1]} years`;
      }
    }
    // Parse work experience patterns
    else if (lowerText.includes('work') || lowerText.includes('experience')) {
      const yearMatch = text.match(/(\d+)\s*year/i);
      if (yearMatch) {
        criteria.name = 'Work Experience';
        criteria.field_type = 'number';
        criteria.operator = lowerText.includes('at least') || lowerText.includes('minimum') ? 'min' : 
                          lowerText.includes('not exceed') || lowerText.includes('maximum') ? 'max' : 'min';
        criteria.value = yearMatch[1];
        criteria.unit = 'years';
        criteria.description = `${criteria.operator === 'min' ? 'Minimum' : 'Maximum'} ${yearMatch[1]} years work experience`;
      }
    }
    // Parse backlogs patterns
    else if (lowerText.includes('backlog') || lowerText.includes('arrear')) {
      const numMatch = text.match(/(\d+)/);
      criteria.name = 'Backlogs';
      criteria.field_type = 'number';
      criteria.operator = 'max';
      criteria.value = numMatch ? numMatch[1] : '0';
      criteria.unit = 'backlogs';
      criteria.description = numMatch ? `Maximum ${numMatch[1]} backlogs allowed` : 'No backlogs allowed';
    }
    
    return criteria;
  };

  const openEligibilityDialog = async (uni) => {
    setSelectedUniversity(uni);
    setSelectedCountry('');
    setCountryCriteriaList([]);
    setNewCriteriaText('');
    setClosedRegions([]);
    setNewClosedRegion('');
    setCountryProgramCriteria([]);
    setSelectedProgramForCriteria(null);
    setProgramCriteriaList([]);
    setNewProgramCriteriaText('');
    setCriteriaFormData({
      degree_type: '',
      min_percentage: '',
      min_gpa: '',
      english_test: '',
      min_english_score: '',
      additional_requirements: ''
    });
    
    // Load existing criteria for this university and programs list
    // FILTER programs by selected university so the dropdown only shows
    // courses that actually belong to this university (e.g. Sunderland).
    try {
      const [criteriaRes, programsRes] = await Promise.all([
        api().get(`/university-eligibility-criteria/${uni.id}`),
        api().get(`/academic-hierarchy/programs?university_id=${uni.id}`)
      ]);
      
      if (criteriaRes.data) {
        setUniversityCriteriaData(criteriaRes.data);
        // Store criteria data for display
        if (criteriaRes.data.country_criteria) {
          const criteriaByCountry = {};
          criteriaRes.data.country_criteria.forEach(cc => {
            criteriaByCountry[cc.country_code || cc.country_name] = cc;
          });
          setEligibilityCriteria(prev => ({...prev, [uni.id]: criteriaByCountry}));
        }
      }
      
      if (programsRes.data) {
        setProgramsList(programsRes.data);
      }
    } catch (error) {
      setUniversityCriteriaData(null);
    }
    setEligibilityDialogOpen(true);
  };

  const loadCountryCriteria = async (countryCode) => {
    setSelectedCountry(countryCode);
    setClosedRegions([]);
    setNewClosedRegion('');
    setCountryProgramCriteria([]);
    setSelectedProgramForCriteria(null);
    setProgramCriteriaList([]);
    
    if (!selectedUniversity || !countryCode) {
      setCountryCriteriaList([]);
      return;
    }
    
    // Load from cached data
    const countryName = countries.find(c => c.id === countryCode)?.name || countryCode;
    const uniCriteria = eligibilityCriteria[selectedUniversity.id];
    
    if (uniCriteria) {
      const countryCrit = uniCriteria[countryName] || uniCriteria[countryCode];
      if (countryCrit && countryCrit.criteria) {
        setCountryCriteriaList(countryCrit.criteria);
      } else {
        setCountryCriteriaList([]);
      }
      // Load closed regions from cached data if available — prefer structured shape
      if (countryCrit && (countryCrit.closed_regions_structured || countryCrit.closed_regions)) {
        setClosedRegions(countryCrit.closed_regions_structured?.length ? countryCrit.closed_regions_structured : (countryCrit.closed_regions || []));
      }
      // Load program criteria from cached data if available
      if (countryCrit && countryCrit.program_criteria) {
        setCountryProgramCriteria(countryCrit.program_criteria);
      }
    } else {
      setCountryCriteriaList([]);
    }
    
    // Also fetch closed regions from backend
    try {
      const res = await api().get(`/university-eligibility-criteria/${selectedUniversity.id}/country/${encodeURIComponent(countryName)}/closed-regions`);
      if (res.data) {
        const structured = res.data.closed_regions_structured;
        const flat = res.data.closed_regions;
        if (Array.isArray(structured) && structured.length) {
          setClosedRegions(structured);
        } else if (Array.isArray(flat)) {
          setClosedRegions(flat);
        }
      }
    } catch (error) {
      // Closed regions not found, keep empty
    }
    
    // Also fetch program criteria for this country from backend
    try {
      const res = await api().get(`/university-eligibility-criteria/${selectedUniversity.id}/country/${encodeURIComponent(countryName)}/program-criteria`);
      if (res.data && res.data.program_criteria) {
        setCountryProgramCriteria(res.data.program_criteria);
      }
    } catch (error) {
      // Program criteria not found, keep empty
    }
  };

  const addCriteriaFromText = () => {
    if (!newCriteriaText.trim()) {
      toast.error('Please enter a criteria');
      return;
    }
    
    const parsedCriteria = parseEnglishCriteria(newCriteriaText);
    parsedCriteria.id = `crit_${Date.now()}`;
    
    setCountryCriteriaList(prev => [...prev, parsedCriteria]);
    setNewCriteriaText('');
    toast.success(`Added: ${parsedCriteria.name}`);
  };

  const removeCriteria = (criteriaId) => {
    setCountryCriteriaList(prev => prev.filter(c => c.id !== criteriaId));
  };

  // ========== CLOSED REGIONS FUNCTIONS ==========
  const addClosedRegion = () => {
    if (!newClosedRegion.trim()) {
      toast.error('Please enter a region name');
      return;
    }
    const regionName = newClosedRegion.trim();
    const regionType = newClosedRegionType || 'custom';
    // Dedupe case-insensitively on (type, name)
    const exists = closedRegions.some((r) => {
      const rname = (typeof r === 'string' ? r : r.name) || '';
      const rtype = (typeof r === 'string' ? 'custom' : r.type) || 'custom';
      return rname.toLowerCase() === regionName.toLowerCase() && rtype === regionType;
    });
    if (exists) {
      toast.error('Region already exists in the list');
      return;
    }
    setClosedRegions((prev) => [...prev, { type: regionType, name: regionName }]);
    setNewClosedRegion('');
  };

  const removeClosedRegion = (target) => {
    setClosedRegions((prev) => prev.filter((r) => {
      if (typeof target === 'string') {
        return (typeof r === 'string' ? r : r.name) !== target;
      }
      const rname = typeof r === 'string' ? r : r.name;
      const rtype = typeof r === 'string' ? 'custom' : r.type;
      return !(rname === target.name && rtype === target.type);
    }));
  };

  const saveClosedRegions = async () => {
    if (!selectedUniversity || !selectedCountry) {
      toast.error('Please select a country first');
      return;
    }
    
    setSaving(true);
    try {
      const countryData = countries.find(c => c.id === selectedCountry);
      const countryName = countryData?.name || selectedCountry;

      // Send the structured payload — backend stores both `closed_regions` (flat
      // names) for legacy back-compat AND `closed_regions_structured` ({type,name}).
      const structured = closedRegions.map((r) =>
        typeof r === 'string' ? { type: 'custom', name: r } : { type: r.type || 'custom', name: r.name }
      );
      await api().put(`/university-eligibility-criteria/${selectedUniversity.id}/country/${encodeURIComponent(countryName)}/closed-regions`, {
        closed_regions: structured,
        country_name: countryName
      });
      
      // Update local eligibility criteria cache
      setEligibilityCriteria(prev => ({
        ...prev,
        [selectedUniversity.id]: {
          ...(prev[selectedUniversity.id] || {}),
          [countryName]: {
            ...(prev[selectedUniversity.id]?.[countryName] || {}),
            closed_regions: closedRegions
          }
        }
      }));
      
      toast.success(`Closed regions saved for ${countryName}`);
    } catch (error) {
      toast.error('Failed to save closed regions');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
  // ========== END CLOSED REGIONS FUNCTIONS ==========

  // ========== PROGRAM SPECIFIC CRITERIA FUNCTIONS (Country-wise) ==========
  const selectProgramForCriteria = (programId) => {
    const program = programsList.find(p => p.id === programId);
    setSelectedProgramForCriteria(program);
    
    // Load existing criteria for this program in the current country
    const existingCrit = countryProgramCriteria.find(pc => pc.program_id === programId);
    if (existingCrit && existingCrit.criteria) {
      setProgramCriteriaList(existingCrit.criteria);
    } else {
      setProgramCriteriaList([]);
    }
  };

  const addProgramCriteria = () => {
    if (!newProgramCriteriaText.trim()) {
      toast.error('Please enter a criteria');
      return;
    }
    
    const parsedCriteria = parseEnglishCriteria(newProgramCriteriaText);
    parsedCriteria.id = `prog_crit_${Date.now()}`;
    
    setProgramCriteriaList(prev => [...prev, parsedCriteria]);
    setNewProgramCriteriaText('');
    toast.success(`Added: ${parsedCriteria.name}`);
  };

  const removeProgramCriteria = (criteriaId) => {
    setProgramCriteriaList(prev => prev.filter(c => c.id !== criteriaId));
  };

  const saveProgramCriteriaForCountry = async () => {
    if (!selectedUniversity || !selectedCountry || !selectedProgramForCriteria) {
      toast.error('Please select a program first');
      return;
    }
    
    setSaving(true);
    try {
      const countryData = countries.find(c => c.id === selectedCountry);
      const countryName = countryData?.name || selectedCountry;
      
      await api().put(`/university-eligibility-criteria/${selectedUniversity.id}/country/${encodeURIComponent(countryName)}/program-criteria/${selectedProgramForCriteria.id}`, {
        criteria: programCriteriaList,
        program_name: selectedProgramForCriteria.name
      });
      
      // Update local cache
      setCountryProgramCriteria(prev => {
        const existingIdx = prev.findIndex(pc => pc.program_id === selectedProgramForCriteria.id);
        const newEntry = {
          program_id: selectedProgramForCriteria.id,
          program_name: selectedProgramForCriteria.name,
          criteria: programCriteriaList
        };
        if (existingIdx >= 0) {
          const updated = [...prev];
          updated[existingIdx] = newEntry;
          return updated;
        }
        return [...prev, newEntry];
      });
      
      toast.success(`Program criteria saved for ${selectedProgramForCriteria.name}`);
    } catch (error) {
      toast.error('Failed to save program criteria');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const deleteProgramCriteriaFromCountry = async (programId) => {
    if (!selectedUniversity || !selectedCountry) return;
    if (!window.confirm('Remove criteria for this program?')) return;
    
    const countryData = countries.find(c => c.id === selectedCountry);
    const countryName = countryData?.name || selectedCountry;
    
    try {
      await api().delete(`/university-eligibility-criteria/${selectedUniversity.id}/country/${encodeURIComponent(countryName)}/program-criteria/${programId}`);
      
      // Update local cache
      setCountryProgramCriteria(prev => prev.filter(pc => pc.program_id !== programId));
      
      if (selectedProgramForCriteria?.id === programId) {
        setSelectedProgramForCriteria(null);
        setProgramCriteriaList([]);
      }
      
      toast.success('Program criteria removed');
    } catch (error) {
      toast.error('Failed to delete program criteria');
    }
  };
  // ========== END PROGRAM SPECIFIC CRITERIA FUNCTIONS ==========

  const saveEligibilityCriteria = async () => {
    if (!selectedUniversity || !selectedCountry) {
      toast.error('Please select a country first');
      return;
    }
    
    if (countryCriteriaList.length === 0) {
      toast.error('Please add at least one criteria');
      return;
    }
    
    setSaving(true);
    try {
      const countryData = countries.find(c => c.id === selectedCountry);
      const countryName = countryData?.name || selectedCountry;
      
      // Get existing criteria for other countries
      let existingCountryCriteria = [];
      if (universityCriteriaData && universityCriteriaData.country_criteria) {
        existingCountryCriteria = universityCriteriaData.country_criteria.filter(
          cc => cc.country_code !== countryName && cc.country_name !== countryName
        );
      }
      
      // r60 — Preserve existing closed_regions for this country so the main
      // "Save Criteria" button doesn't wipe out regions the user added via
      // the dedicated "Save Closed Regions" button. Same for program_criteria.
      // We read the latest values from `universityCriteriaData` (fresh from
      // backend) plus the in-memory `closedRegions` array — whichever is
      // longest wins so an unsaved local edit isn't lost either.
      const existingCcForCountry = (universityCriteriaData?.country_criteria || []).find(
        cc => (cc.country_code || '').toLowerCase() === countryName.toLowerCase()
           || (cc.country_name || '').toLowerCase() === countryName.toLowerCase()
      ) || {};
      const localStructured = (closedRegions || []).map(r =>
        typeof r === 'string' ? { type: 'custom', name: r } : { type: r.type || 'custom', name: r.name }
      ).filter(r => r.name);
      const persistedStructured = existingCcForCountry.closed_regions_structured || [];
      const preservedStructured = localStructured.length >= persistedStructured.length
        ? localStructured
        : persistedStructured;
      const preservedFlat = preservedStructured.map(r => r.name);

      // Add/update criteria for selected country
      const newCountryCriteria = {
        country_code: countryName,
        country_name: countryName,
        degree_type_id: null,
        degree_type_name: 'All Degrees',
        criteria: countryCriteriaList.map(c => ({
          id: c.id || `crit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: c.name,
          field_type: c.field_type,
          operator: c.operator,
          value: c.value,
          unit: c.unit || null,
          description: c.description || c.original_text || c.name
        })),
        closed_regions: preservedFlat,
        closed_regions_structured: preservedStructured,
        program_criteria: existingCcForCountry.program_criteria || [],
        is_default: false
      };
      
      await api().put(`/university-eligibility-criteria/${selectedUniversity.id}/country`, {
        country_criteria: [...existingCountryCriteria, newCountryCriteria]
      });
      
      // Update local state
      setEligibilityCriteria(prev => ({
        ...prev,
        [selectedUniversity.id]: {
          ...(prev[selectedUniversity.id] || {}),
          [countryName]: newCountryCriteria
        }
      }));
      
      // Reload university criteria
      try {
        const res = await api().get(`/university-eligibility-criteria/${selectedUniversity.id}`);
        if (res.data) {
          setUniversityCriteriaData(res.data);
        }
      } catch (e) {}
      
      toast.success('Eligibility criteria saved successfully');
    } catch (error) {
      toast.error('Failed to save criteria');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const getUniversityCriteriaCount = (uniId) => {
    const criteria = eligibilityCriteria[uniId];
    if (!criteria || typeof criteria !== 'object') return 0;
    return Object.keys(criteria).length;
  };

  const getOperatorSymbol = (op) => {
    switch(op) {
      case 'min': return '≥';
      case 'max': return '≤';
      case 'equals': return '=';
      default: return '';
    }
  };

  const renderEligibilityContent = () => {
    if (customLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-xl font-semibold">Eligibility Criteria per University</h3>
          <p className="text-sm text-muted-foreground">Configure country-wise admission requirements for AI vetting. These criteria are used to automatically vet student applications.</p>
        </div>
        
        <Card className="border-dashed border-2 bg-muted/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-primary/10">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">How It Works</h4>
                <p className="text-sm text-muted-foreground">
                  Set minimum academic requirements (percentage, GPA) and English test scores for each country. 
                  The AI counselor will use these criteria to vet student applications automatically.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="border rounded-lg divide-y">
          {universities.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No universities configured yet</p>
              <p className="text-sm">Add universities in the Master Data section first</p>
            </div>
          ) : (
            universities.map((uni) => {
              const criteriaCount = getUniversityCriteriaCount(uni.id);
              return (
                <div key={uni.id} className="flex items-center justify-between p-4 hover:bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Target className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{uni.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {criteriaCount > 0 
                          ? `${criteriaCount} country criteria configured` 
                          : 'No criteria configured yet'}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => openEligibilityDialog(uni)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Configure
                  </Button>
                </div>
              );
            })
          )}
        </div>

        {/* Eligibility Criteria Dialog */}
        <Dialog open={eligibilityDialogOpen} onOpenChange={setEligibilityDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Eligibility Criteria</DialogTitle>
              <DialogDescription>{selectedUniversity?.name} - Configure country-wise requirements</DialogDescription>
            </DialogHeader>
            
            {/* Show existing criteria for this university */}
            {universityCriteriaData && universityCriteriaData.country_criteria && universityCriteriaData.country_criteria.length > 0 && (
              <div className="bg-muted/30 rounded-lg p-4 mb-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Configured Countries ({universityCriteriaData.country_criteria.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {universityCriteriaData.country_criteria.map((cc, idx) => (
                    <Badge key={idx} variant="secondary" className="cursor-pointer hover:bg-primary hover:text-white" 
                      onClick={() => {
                        const country = countries.find(c => c.name === cc.country_name || c.name === cc.country_code);
                        if (country) loadCountryCriteria(country.id);
                      }}>
                      {cc.country_name || cc.country_code} ({cc.criteria?.length || 0} rules)
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Select Country *</Label>
                <Select value={selectedCountry} onValueChange={loadCountryCriteria}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a country to configure" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.id} value={country.id}>{country.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedCountry && (
                <>
                  <Separator />
                  
                  {/* Plain English Criteria Input */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      Add Criteria in Plain English
                      <Badge variant="outline" className="text-xs">AI Parsed</Badge>
                    </Label>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="e.g., Educational gap should not be more than 5 years"
                        value={newCriteriaText}
                        onChange={(e) => setNewCriteriaText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addCriteriaFromText()}
                        className="flex-1"
                      />
                      <Button onClick={addCriteriaFromText} size="sm">
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Examples: "Minimum 60% marks", "IELTS score at least 6.5", "No backlogs allowed", "Work experience minimum 2 years"
                    </p>
                  </div>
                  
                  {/* Current Criteria List */}
                  <div className="space-y-2">
                    <Label>Current Criteria ({countryCriteriaList.length})</Label>
                    {countryCriteriaList.length === 0 ? (
                      <div className="text-center py-6 border-2 border-dashed rounded-lg text-muted-foreground">
                        <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No criteria added yet</p>
                        <p className="text-xs">Add criteria using the input above or quick add buttons</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {countryCriteriaList.map((crit, idx) => (
                          <div key={crit.id || idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant={crit.operator === 'min' ? 'default' : crit.operator === 'max' ? 'destructive' : 'secondary'}>
                                  {getOperatorSymbol(crit.operator)} {crit.value}{crit.unit ? ` ${crit.unit}` : ''}
                                </Badge>
                                <span className="font-medium">{crit.name}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">{crit.description}</p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => removeCriteria(crit.id)}>
                              <X className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <Separator />
                  
                  {/* Quick Add Buttons */}
                  <div className="space-y-2">
                    <Label>Quick Add Common Criteria</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setNewCriteriaText('Minimum 60% marks required'); }}>
                        60% Marks
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setNewCriteriaText('Minimum GPA 3.0 required'); }}>
                        GPA 3.0
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setNewCriteriaText('IELTS score minimum 6.5'); }}>
                        IELTS 6.5
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setNewCriteriaText('Educational gap should not exceed 5 years'); }}>
                        Gap ≤5 Years
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setNewCriteriaText('No backlogs allowed'); }}>
                        No Backlogs
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setNewCriteriaText('Age should not exceed 35 years'); }}>
                        Age ≤35
                      </Button>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  {/* Closed Regions Section */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Ban className="w-4 h-4 text-destructive" />
                      <Label className="text-base font-semibold">Closed Regions</Label>
                      <Badge variant="destructive" className="text-xs">Credibility Check</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Applications from students whose passport "Place of Birth" matches any of these regions will be automatically flagged during the credibility check.
                    </p>
                    
                    <div className="flex gap-2">
                      <Select value={newClosedRegionType} onValueChange={setNewClosedRegionType}>
                        <SelectTrigger className="w-[140px]" data-testid="closed-region-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="city">City</SelectItem>
                          <SelectItem value="province">Province / State</SelectItem>
                          <SelectItem value="district">District</SelectItem>
                          <SelectItem value="country">Country</SelectItem>
                          <SelectItem value="custom">Custom Label</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input 
                        placeholder="e.g., Kashmir, Punjab, Sindh..."
                        value={newClosedRegion}
                        onChange={(e) => setNewClosedRegion(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addClosedRegion()}
                        className="flex-1"
                        data-testid="closed-region-input"
                      />
                      <Button onClick={addClosedRegion} size="sm" variant="destructive" data-testid="closed-region-add-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    
                    {closedRegions.length === 0 ? (
                      <div className="text-center py-4 border-2 border-dashed rounded-lg text-muted-foreground">
                        <Ban className="w-6 h-6 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No closed regions configured</p>
                        <p className="text-xs">All regions are currently allowed</p>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {closedRegions.map((region, idx) => {
                          const isStr = typeof region === 'string';
                          const rname = isStr ? region : region.name;
                          const rtype = isStr ? 'custom' : (region.type || 'custom');
                          const typeColor = {
                            city: 'bg-blue-500',
                            province: 'bg-amber-500',
                            district: 'bg-amber-500',
                            country: 'bg-amber-500',
                            custom: 'bg-zinc-500',
                          }[rtype] || 'bg-zinc-500';
                          return (
                            <Badge
                              key={`${rtype}::${rname}::${idx}`}
                              variant="destructive"
                              className="py-1.5 px-3 flex items-center gap-2"
                              data-testid={`closed-region-chip-${idx}`}
                            >
                              <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded ${typeColor} text-white`}>
                                {rtype}
                              </span>
                              <AlertTriangle className="w-3 h-3" />
                              {rname}
                              <button 
                                onClick={() => removeClosedRegion(isStr ? region : { type: rtype, name: rname })}
                                className="ml-1 hover:bg-white/20 rounded p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                    
                    {closedRegions.length > 0 && (
                      <Button 
                        onClick={saveClosedRegions} 
                        disabled={saving} 
                        variant="outline"
                        className="w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      >
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        <Ban className="w-4 h-4 mr-2" />
                        Save Closed Regions ({closedRegions.length})
                      </Button>
                    )}
                  </div>
                  
                  <Separator />
                  
                  {/* Program Specific Criteria Section */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-amber-500" />
                      <Label className="text-base font-semibold">Program Specific Criteria</Label>
                      <Badge variant="outline" className="text-xs bg-amber-50 text-slate-800 border-amber-200">AI Verified</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Configure additional criteria for specific programs. These are checked alongside country criteria during credibility report generation.
                    </p>
                    
                    {/* Existing Program Criteria */}
                    {countryProgramCriteria.length > 0 && (
                      <div className="bg-amber-50/50 dark:bg-slate-900/20 rounded-lg p-3">
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <GraduationCap className="w-3.5 h-3.5" />
                          Programs with Criteria ({countryProgramCriteria.length})
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {countryProgramCriteria.map((pc, idx) => (
                            <Badge 
                              key={idx} 
                              variant="secondary" 
                              className="cursor-pointer hover:bg-amber-200 py-1.5 flex items-center gap-1"
                              onClick={() => selectProgramForCriteria(pc.program_id)}
                            >
                              {pc.program_name} ({pc.criteria?.length || 0} rules)
                              <button 
                                onClick={(e) => { e.stopPropagation(); deleteProgramCriteriaFromCountry(pc.program_id); }}
                                className="ml-1 hover:bg-red-500 rounded p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label>Select Program</Label>
                      <SearchableSelect 
                        options={programsList.map((prog) => ({
                          value: prog.id,
                          label: `${prog.name}${prog.degree_type_name ? ` (${prog.degree_type_name})` : ''}`
                        }))}
                        value={selectedProgramForCriteria?.id || ''}
                        onValueChange={selectProgramForCriteria}
                        placeholder="Search and select a program..."
                        searchPlaceholder="Type to search programs..."
                        emptyText="No programs found."
                      />
                    </div>
                    
                    {selectedProgramForCriteria && (
                      <>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            Criteria for {selectedProgramForCriteria.name}
                          </Label>
                          <div className="flex gap-2">
                            <Input 
                              placeholder="e.g., Minimum 2 years work experience"
                              value={newProgramCriteriaText}
                              onChange={(e) => setNewProgramCriteriaText(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && addProgramCriteria()}
                              className="flex-1"
                            />
                            <Button onClick={addProgramCriteria} size="sm" variant="outline">
                              <Plus className="w-4 h-4 mr-1" />
                              Add
                            </Button>
                          </div>
                        </div>
                        
                        {programCriteriaList.length === 0 ? (
                          <div className="text-center py-4 border-2 border-dashed rounded-lg text-muted-foreground">
                            <GraduationCap className="w-6 h-6 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">No criteria for this program</p>
                            <p className="text-xs">Add criteria using the input above</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[150px] overflow-y-auto">
                            {programCriteriaList.map((crit, idx) => (
                              <div key={crit.id || idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <Badge variant={crit.operator === 'min' ? 'default' : crit.operator === 'max' ? 'destructive' : 'secondary'} className="text-xs">
                                      {getOperatorSymbol(crit.operator)} {crit.value}{crit.unit ? ` ${crit.unit}` : ''}
                                    </Badge>
                                    <span className="font-medium text-sm">{crit.name}</span>
                                  </div>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => removeProgramCriteria(crit.id)}>
                                  <X className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Quick Add for Programs */}
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setNewProgramCriteriaText('Minimum 2 years work experience'); }}>
                            2 Yrs Exp
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setNewProgramCriteriaText('Minimum 70% marks required'); }}>
                            70% Marks
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setNewProgramCriteriaText('IELTS score minimum 7.0'); }}>
                            IELTS 7.0
                          </Button>
                        </div>
                        
                        {programCriteriaList.length > 0 && (
                          <Button 
                            onClick={saveProgramCriteriaForCountry} 
                            disabled={saving} 
                            variant="outline"
                            className="w-full border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-white"
                          >
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <GraduationCap className="w-4 h-4 mr-2" />
                            Save Program Criteria ({programCriteriaList.length})
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEligibilityDialogOpen(false)}>Cancel</Button>
              <Button onClick={saveEligibilityCriteria} disabled={saving || !selectedCountry || countryCriteriaList.length === 0}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Criteria ({countryCriteriaList.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // Render automation content
  const renderAutomationContent = () => {
    if (customLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <h3 className="font-semibold">Automation Rules</h3>
          <p className="text-sm text-muted-foreground">Configure automatic email and SMS notifications</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-4 h-4" />
              New Application Welcome
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-sm">Send welcome message when application is created</span>
              <Switch checked={automationSettings?.new_application?.enabled || false} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Document Shortfall
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-sm">Notify when documents are missing</span>
              <Switch checked={automationSettings?.document_shortfall?.enabled || false} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CircleDot className="w-4 h-4" />
              Status Change Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Configure notifications for specific status changes in SMTP Settings
            </p>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render master data content
  const renderMasterDataContent = () => {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              {activeConfig?.icon && <activeConfig.icon className="w-5 h-5" />}
              {activeConfig?.label}
            </h3>
            <p className="text-sm text-muted-foreground">{activeConfig?.description}</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedItems.length > 0 && (
              <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedItems.length})
              </Button>
            )}
            <CSVExportButton entityType={activeTab} entityLabel={activeConfig?.label || 'Items'} />
            <CSVImportModal entityType={activeTab} entityLabel={activeConfig?.label || 'Items'} onSuccess={() => api().get(`/master-data?type=${activeTab}`).then(res => setData(res.data))} />
            {activeTab === 'country' && (
              <Button 
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await api().post('/master-data/seed-countries');
                    toast.success(res.data.message || 'Countries loaded successfully');
                    const response = await api().get('/master-data?type=country');
                    setData(response.data);
                  } catch (error) {
                    toast.error('Failed to load countries');
                  }
                }}
              >
                <Globe className="w-4 h-4 mr-2" />
                Load All Countries
              </Button>
            )}
            <Button onClick={() => { setFormData({ name: '', parent_id: '', metadata: {} }); setPendingLogoFile(null); setLogoPreviewBlob(null); setCreateOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add New
            </Button>
          </div>
        </div>

        {/* r60 — Per-table free-text search. Matches on name, parent name,
            subject_name and faculty_name (the four visible columns for the
            Programs tab). Stays empty when there's nothing to filter. */}
        {data.length > 0 && (
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              type="search"
              placeholder={`Search ${activeConfig?.label?.toLowerCase() || 'items'}…`}
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              className="pl-9 pr-9"
              data-testid="master-data-table-search"
            />
            {tableSearch && (
              <button
                type="button"
                onClick={() => setTableSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
                data-testid="master-data-table-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {(() => {
          // Pre-compute the filtered rows so the table body and the
          // select-all checkbox both use the same list. Match is
          // case-insensitive across every column the table shows.
          const q = tableSearch.trim().toLowerCase();
          const filteredData = !q ? data : data.filter((item) => {
            const haystack = [
              item.name,
              item.parent_name,
              item.metadata?.subject_name,
              item.metadata?.faculty_name,
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(q);
          });
          return (
        <div className="border rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : data.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Database className="w-12 h-12 mb-3 opacity-30" />
              <p>No {activeConfig?.label?.toLowerCase()} found</p>
              <p className="text-sm">Add your first item to get started</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground" data-testid="master-data-table-no-results">
              <Search className="w-12 h-12 mb-3 opacity-30" />
              <p>No matches for &ldquo;{tableSearch}&rdquo;</p>
              <Button variant="link" size="sm" onClick={() => setTableSearch('')}>Clear search</Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedItems.length === filteredData.length && filteredData.length > 0}
                      onCheckedChange={() =>
                        setSelectedItems(selectedItems.length === filteredData.length ? [] : filteredData.map(i => i.id))
                      }
                    />
                  </TableHead>
                  <TableHead>
                    Name
                    {q && <span className="ml-2 text-xs font-normal text-muted-foreground">({filteredData.length} of {data.length})</span>}
                  </TableHead>
                  {needsParent && <TableHead>Parent</TableHead>}
                  {activeTab === 'program' && <TableHead>Subject</TableHead>}
                  {activeTab === 'program' && <TableHead>Faculty</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((item) => (
                  <TableRow key={item.id} className={selectedItems.includes(item.id) ? 'bg-muted/50' : ''}>
                    <TableCell>
                      <Checkbox
                        checked={selectedItems.includes(item.id)}
                        onCheckedChange={() => toggleItemSelection(item.id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    {needsParent && (
                      <TableCell>
                        <Badge variant="outline">{item.parent_name || '-'}</Badge>
                      </TableCell>
                    )}
                    {activeTab === 'program' && (
                      <TableCell className="text-sm text-muted-foreground">
                        {item.metadata?.subject_name || '-'}
                      </TableCell>
                    )}
                    {activeTab === 'program' && (
                      <TableCell className="text-sm text-muted-foreground">
                        {item.metadata?.faculty_name || '-'}
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge variant={item.status === 'active' ? 'default' : 'secondary'}>
                        {item.status || 'active'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(item)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
          );
        })()}
      </div>
    );
  };

  // Data Backup Content
  const [backupPreview, setBackupPreview] = useState(null);
  const [loadingBackup, setLoadingBackup] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);

  const fetchBackupPreview = async () => {
    setLoadingBackup(true);
    try {
      const response = await api().get('/data-backup/preview');
      setBackupPreview(response.data);
    } catch (error) {
      toast.error('Failed to load backup preview');
    } finally {
      setLoadingBackup(false);
    }
  };

  const downloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const response = await api().get('/data-backup/download', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download backup');
    } finally {
      setDownloadingBackup(false);
    }
  };

  // Academic Hierarchy Functions
  const fetchHierarchyData = async () => {
    setHierarchyLoading(true);
    try {
      const [uniRes, campusRes, dtRes, progRes] = await Promise.all([
        api().get('/academic-hierarchy/universities'),
        api().get('/academic-hierarchy/campuses'),
        api().get('/academic-hierarchy/degree-types'),
        api().get('/academic-hierarchy/programs')
      ]);
      setHierarchyUniversities(uniRes.data);
      setHierarchyCampuses(campusRes.data);
      setHierarchyDegreeTypes(dtRes.data);
      setHierarchyPrograms(progRes.data);
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setHierarchyLoading(false);
    }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setCsvImporting(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await api().post('/academic-hierarchy/import-csv', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { success, universities_created, campuses_created, degree_types_created, programs_created, errors } = response.data;
      
      if (success > 0) {
        toast.success(`Import successful! Created: ${universities_created} universities, ${campuses_created} campuses, ${degree_types_created} degree types, ${programs_created} programs`);
        fetchHierarchyData();
      }
      
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} row(s) had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setCsvImporting(false);
      e.target.value = '';
    }
  };

  const downloadCsvTemplate = async () => {
    try {
      const response = await api().get('/academic-hierarchy/export-template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'academic_hierarchy_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const downloadAllData = async () => {
    try {
      const response = await api().get('/academic-hierarchy/export-data', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `academic_hierarchy_data_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleHierarchyCreate = async () => {
    setSaving(true);
    try {
      let parentId = null;
      let metadata = {};
      
      if (hierarchyCreateType === 'campus') {
        parentId = selectedHierarchyUniversity;
        metadata = { university_name: hierarchyUniversities.find(u => u.id === selectedHierarchyUniversity)?.name };
      } else if (hierarchyCreateType === 'degree_type') {
        parentId = selectedHierarchyCampus || selectedHierarchyUniversity;
      } else if (hierarchyCreateType === 'program') {
        parentId = selectedHierarchyDegreeType || selectedHierarchyCampus || selectedHierarchyUniversity;
        metadata = {
          subject: hierarchyFormData.subject,
          faculty: hierarchyFormData.faculty,
          duration: hierarchyFormData.duration,
          fees: hierarchyFormData.fees
        };
      }
      
      await api().post('/master-data', {
        type: hierarchyCreateType,
        name: hierarchyFormData[hierarchyCreateType === 'degree_type' ? 'degree_type' : `${hierarchyCreateType}_name`] || hierarchyFormData.program_name,
        parent_id: parentId,
        metadata
      });
      
      toast.success(`${hierarchyCreateType.replace('_', ' ')} created successfully`);
      setHierarchyCreateOpen(false);
      setHierarchyFormData({ university_name: '', campus_name: '', degree_type: '', program_name: '', subject: '', faculty: '', duration: '', fees: '' });
      fetchHierarchyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create item');
    } finally {
      setSaving(false);
    }
  };

  const getCampusesForUniversity = (uniId) => hierarchyCampuses.filter(c => c.parent_id === uniId);
  const getDegreeTypesForCampus = (campusId) => hierarchyDegreeTypes.filter(dt => dt.parent_id === campusId);
  const getProgramsForDegreeType = (dtId) => hierarchyPrograms.filter(p => p.parent_id === dtId);

  const renderAcademicHierarchyContent = () => {
    if (customLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold">Academic Hierarchy Manager</h2>
            <p className="text-muted-foreground">Manage University → Campus → Degree Type → Program structure</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadCsvTemplate}>
              <Download className="w-4 h-4 mr-2" />
              CSV Template
            </Button>
            <Button variant="default" onClick={downloadAllData} data-testid="export-all-data-btn">
              <Download className="w-4 h-4 mr-2" />
              Export All Data
            </Button>
            <div className="relative">
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvImport}
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={csvImporting}
              />
              <Button variant="outline" disabled={csvImporting}>
                {csvImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Import CSV
              </Button>
            </div>
            <Button onClick={fetchHierarchyData} variant="outline" disabled={hierarchyLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${hierarchyLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Single-Click Excel Importer — new */}
        <Card className="border-2 border-[#F5A623]/30 bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="p-2.5 rounded-lg bg-[#F5A623] text-white shrink-0">
                <FileText className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-bold text-[#1A2A4F]">Single-Click Excel Import</h4>
                  <span className="text-[10px] uppercase tracking-wider font-bold bg-[#F5A623] text-white px-1.5 py-0.5 rounded">New</span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Upload one Excel/CSV containing universities, campuses, programs, currencies, sessions and more — the system auto-matches existing records and creates the missing ones. No duplicates.
                </p>
                <div className="flex items-center gap-2">
                  <a
                    href="/import/courses"
                    className="inline-flex items-center gap-2 bg-[#F5A623] text-white text-sm font-bold px-4 py-2 rounded-md hover:brightness-95"
                    data-testid="core-settings-open-importer"
                  >
                    Open Importer →
                  </a>
                  <span className="text-xs text-muted-foreground">
                    22 columns supported · Drag-drop · Preview before import · Idempotent
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sample Data Download */}
        <Card className="border-green-200 bg-green-50/50 dark:bg-green-950/20 dark:border-green-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <GraduationCap className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">University of Sunderland - Complete Course Data</h4>
                  <p className="text-sm text-muted-foreground">
                    Pre-scraped data with 122 programs including all campuses, degree types, subjects, and faculties.
                  </p>
                </div>
              </div>
              <a 
                href="/university_of_sunderland_courses.csv" 
                download="university_of_sunderland_courses.csv"
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-green-600 text-white hover:bg-green-700 h-10 px-4 py-2"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Sunderland CSV
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Bangor University Quick Import */}
        <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <GraduationCap className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Bangor University - Complete Course Data</h4>
                  <p className="text-sm text-muted-foreground">
                    Scraped from bangor.ac.uk - 200+ programs including BSc, BA, MSc, MA, MBA, PhD, and more.
                  </p>
                </div>
              </div>
              <Button 
                onClick={async () => {
                  try {
                    const res = await api().post('/master-data/seed-bangor-courses');
                    toast.success(res.data.message || 'Bangor courses imported successfully');
                    fetchHierarchyData();
                  } catch (error) {
                    toast.error('Failed to import Bangor courses');
                  }
                }}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Import Bangor Courses
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Add Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button 
            variant="outline" 
            className="h-auto py-4 flex-col gap-2"
            onClick={() => { setHierarchyCreateType('university'); setHierarchyCreateOpen(true); }}
          >
            <Building2 className="w-6 h-6 text-blue-500" />
            <span>Add University</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex-col gap-2"
            onClick={() => { setHierarchyCreateType('campus'); setHierarchyCreateOpen(true); }}
            disabled={hierarchyUniversities.length === 0}
          >
            <MapPin className="w-6 h-6 text-green-500" />
            <span>Add Campus</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex-col gap-2"
            onClick={() => { setHierarchyCreateType('degree_type'); setHierarchyCreateOpen(true); }}
            disabled={hierarchyUniversities.length === 0}
          >
            <GraduationCap className="w-6 h-6 text-amber-500" />
            <span>Add Degree Type</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-auto py-4 flex-col gap-2"
            onClick={() => { setHierarchyCreateType('program'); setHierarchyCreateOpen(true); }}
            disabled={hierarchyUniversities.length === 0}
          >
            <FileText className="w-6 h-6 text-orange-500" />
            <span>Add Program</span>
          </Button>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-500">{hierarchyUniversities.length}</div>
              <p className="text-sm text-muted-foreground">Universities</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-500">{hierarchyCampuses.length}</div>
              <p className="text-sm text-muted-foreground">Campuses</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-amber-500">{hierarchyDegreeTypes.length}</div>
              <p className="text-sm text-muted-foreground">Degree Types</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-500">{hierarchyPrograms.length}</div>
              <p className="text-sm text-muted-foreground">Programs</p>
            </CardContent>
          </Card>
        </div>

        {/* Hierarchy Tree View */}
        <Card>
          <CardHeader>
            <CardTitle>Academic Structure</CardTitle>
            <CardDescription>Hierarchical view of your academic data</CardDescription>
          </CardHeader>
          <CardContent>
            {hierarchyUniversities.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No universities configured yet</p>
                <p className="text-sm">Add universities manually or import via CSV</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-4">
                  {hierarchyUniversities.map((uni) => {
                    const campuses = getCampusesForUniversity(uni.id);
                    return (
                      <div key={uni.id} className="border rounded-lg p-4">
                        <div className="flex items-center gap-2 font-semibold text-blue-600">
                          <Building2 className="w-5 h-5" />
                          {uni.name}
                        </div>
                        {campuses.length > 0 ? (
                          <div className="ml-6 mt-3 space-y-3">
                            {campuses.map((campus) => {
                              const degreeTypes = getDegreeTypesForCampus(campus.id);
                              return (
                                <div key={campus.id} className="border-l-2 border-green-300 pl-4">
                                  <div className="flex items-center gap-2 text-green-600 font-medium">
                                    <MapPin className="w-4 h-4" />
                                    {campus.name}
                                  </div>
                                  {degreeTypes.length > 0 ? (
                                    <div className="ml-4 mt-2 space-y-2">
                                      {degreeTypes.map((dt) => {
                                        const programs = getProgramsForDegreeType(dt.id);
                                        return (
                                          <div key={dt.id} className="border-l-2 border-amber-300 pl-4">
                                            <div className="flex items-center gap-2 text-amber-500">
                                              <GraduationCap className="w-4 h-4" />
                                              {dt.name}
                                            </div>
                                            {programs.length > 0 && (
                                              <div className="ml-4 mt-1 space-y-1">
                                                {programs.map((prog) => (
                                                  <div key={prog.id} className="flex items-center gap-2 text-orange-600 text-sm">
                                                    <FileText className="w-3 h-3" />
                                                    {prog.name}
                                                    {prog.metadata?.subject && (
                                                      <Badge variant="outline" className="text-xs">{prog.metadata.subject}</Badge>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <p className="ml-4 mt-1 text-xs text-muted-foreground">No degree types</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="ml-6 mt-2 text-sm text-muted-foreground">No campuses configured</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={hierarchyCreateOpen} onOpenChange={setHierarchyCreateOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add {hierarchyCreateType?.replace('_', ' ')}</DialogTitle>
              <DialogDescription>
                {hierarchyCreateType === 'university' && 'Add a new university to your academic structure'}
                {hierarchyCreateType === 'campus' && 'Add a campus to a university'}
                {hierarchyCreateType === 'degree_type' && 'Add a degree type to a campus'}
                {hierarchyCreateType === 'program' && 'Add a program with subject and faculty'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {hierarchyCreateType === 'university' && (
                <div className="space-y-2">
                  <Label>University Name *</Label>
                  <Input
                    value={hierarchyFormData.university_name}
                    onChange={(e) => setHierarchyFormData({ ...hierarchyFormData, university_name: e.target.value })}
                    placeholder="e.g., Oxford University"
                  />
                </div>
              )}

              {hierarchyCreateType === 'campus' && (
                <>
                  <div className="space-y-2">
                    <Label>Select University *</Label>
                    <Select value={selectedHierarchyUniversity} onValueChange={setSelectedHierarchyUniversity}>
                      <SelectTrigger><SelectValue placeholder="Select university" /></SelectTrigger>
                      <SelectContent>
                        {hierarchyUniversities.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Campus Name *</Label>
                    <Input
                      value={hierarchyFormData.campus_name}
                      onChange={(e) => setHierarchyFormData({ ...hierarchyFormData, campus_name: e.target.value })}
                      placeholder="e.g., Main Campus"
                    />
                  </div>
                </>
              )}

              {hierarchyCreateType === 'degree_type' && (
                <>
                  <div className="space-y-2">
                    <Label>Select University *</Label>
                    <Select value={selectedHierarchyUniversity} onValueChange={(v) => { setSelectedHierarchyUniversity(v); setSelectedHierarchyCampus(''); }}>
                      <SelectTrigger><SelectValue placeholder="Select university" /></SelectTrigger>
                      <SelectContent>
                        {hierarchyUniversities.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedHierarchyUniversity && (
                    <div className="space-y-2">
                      <Label>Select Campus (Optional)</Label>
                      <Select value={selectedHierarchyCampus} onValueChange={setSelectedHierarchyCampus}>
                        <SelectTrigger><SelectValue placeholder="Select campus" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All Campuses</SelectItem>
                          {getCampusesForUniversity(selectedHierarchyUniversity).map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Degree Type Name *</Label>
                    <Input
                      value={hierarchyFormData.degree_type}
                      onChange={(e) => setHierarchyFormData({ ...hierarchyFormData, degree_type: e.target.value })}
                      placeholder="e.g., Undergraduate, Postgraduate"
                    />
                  </div>
                </>
              )}

              {hierarchyCreateType === 'program' && (
                <>
                  <div className="space-y-2">
                    <Label>Select University *</Label>
                    <Select value={selectedHierarchyUniversity} onValueChange={(v) => { setSelectedHierarchyUniversity(v); setSelectedHierarchyCampus(''); setSelectedHierarchyDegreeType(''); }}>
                      <SelectTrigger><SelectValue placeholder="Select university" /></SelectTrigger>
                      <SelectContent>
                        {hierarchyUniversities.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedHierarchyUniversity && (
                    <div className="space-y-2">
                      <Label>Select Campus (Optional)</Label>
                      <Select value={selectedHierarchyCampus} onValueChange={(v) => { setSelectedHierarchyCampus(v); setSelectedHierarchyDegreeType(''); }}>
                        <SelectTrigger><SelectValue placeholder="Select campus" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All Campuses</SelectItem>
                          {getCampusesForUniversity(selectedHierarchyUniversity).map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {(selectedHierarchyCampus || selectedHierarchyUniversity) && (
                    <div className="space-y-2">
                      <Label>Select Degree Type (Optional)</Label>
                      <Select value={selectedHierarchyDegreeType} onValueChange={setSelectedHierarchyDegreeType}>
                        <SelectTrigger><SelectValue placeholder="Select degree type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All Degree Types</SelectItem>
                          {(selectedHierarchyCampus ? getDegreeTypesForCampus(selectedHierarchyCampus) : hierarchyDegreeTypes.filter(dt => getCampusesForUniversity(selectedHierarchyUniversity).some(c => c.id === dt.parent_id))).map((dt) => (
                            <SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Program Name *</Label>
                    <Input
                      value={hierarchyFormData.program_name}
                      onChange={(e) => setHierarchyFormData({ ...hierarchyFormData, program_name: e.target.value })}
                      placeholder="e.g., Computer Science BSc"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Subject <Badge variant="outline" className="text-xs ml-1">Auto-fill</Badge></Label>
                      <Input
                        value={hierarchyFormData.subject}
                        onChange={(e) => setHierarchyFormData({ ...hierarchyFormData, subject: e.target.value })}
                        placeholder="e.g., Computer Science"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Faculty <Badge variant="outline" className="text-xs ml-1">Auto-fill</Badge></Label>
                      <Input
                        value={hierarchyFormData.faculty}
                        onChange={(e) => setHierarchyFormData({ ...hierarchyFormData, faculty: e.target.value })}
                        placeholder="e.g., Faculty of Engineering"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Duration</Label>
                      <Input
                        value={hierarchyFormData.duration}
                        onChange={(e) => setHierarchyFormData({ ...hierarchyFormData, duration: e.target.value })}
                        placeholder="e.g., 3 years"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fees</Label>
                      <Input
                        value={hierarchyFormData.fees}
                        onChange={(e) => setHierarchyFormData({ ...hierarchyFormData, fees: e.target.value })}
                        placeholder="e.g., £9,250/year"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHierarchyCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleHierarchyCreate} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  const renderDataBackupContent = () => {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Data Backup</h2>
          <p className="text-muted-foreground">Download a complete backup of your organization's data</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Backup Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Archive className="w-5 h-5 text-primary" />
                Full Data Export
              </CardTitle>
              <CardDescription>
                Download all your organization data as a ZIP file containing JSON files
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <p className="font-medium">What's included:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Applications & Lead data</li>
                  <li>User accounts & Roles</li>
                  <li>Master data (Universities, Programs, etc.)</li>
                  <li>Forms & Submissions</li>
                  <li>Email/SMS templates & logs</li>
                  <li>AI session history</li>
                  <li>Activity logs & Notifications</li>
                  <li>Billing & Credit transactions</li>
                </ul>
              </div>
              
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Document files (PDFs, images) are not included. Only file metadata and references are exported.</span>
                </p>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={fetchBackupPreview}
                  disabled={loadingBackup}
                >
                  {loadingBackup ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Preview Data
                </Button>
                <Button 
                  onClick={downloadBackup}
                  disabled={downloadingBackup}
                >
                  {downloadingBackup ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download Backup
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-emerald-500" />
                Data Preview
              </CardTitle>
              <CardDescription>
                See what will be included in your backup
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!backupPreview ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Database className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Click "Preview Data" to see backup details</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                    <span className="font-medium">Total Records</span>
                    <Badge variant="secondary" className="text-lg">
                      {backupPreview.total_records?.toLocaleString()}
                    </Badge>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                    <span className="font-medium">Estimated Size</span>
                    <Badge variant="outline">
                      ~{backupPreview.estimated_size_mb} MB
                    </Badge>
                  </div>

                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {Object.entries(backupPreview.collections || {}).map(([name, count]) => (
                        <div key={name} className="flex justify-between items-center py-1 px-2 hover:bg-muted/50 rounded">
                          <span className="text-sm capitalize">{name.replace(/_/g, ' ')}</span>
                          <span className="text-sm text-muted-foreground">{count} records</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // Fetch agent permissions
  const fetchAgentPermissions = async () => {
    setAgentPermissionsLoading(true);
    try {
      const response = await api().get('/agent-permissions');
      setAgentPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch agent permissions:', error);
    } finally {
      setAgentPermissionsLoading(false);
    }
  };

  // Save agent permissions
  const saveAgentPermissions = async () => {
    setSavingAgentPermissions(true);
    try {
      await api().put('/agent-permissions', agentPermissions);
      toast.success('Agent permissions saved successfully');
    } catch (error) {
      toast.error('Failed to save agent permissions');
    } finally {
      setSavingAgentPermissions(false);
    }
  };

  // Fetch agent permissions when tab is active
  useEffect(() => {
    if (activeTab === 'agent_permissions') {
      fetchAgentPermissions();
    }
  }, [activeTab]);

  // Render Agent Permissions Content
  const renderAgentPermissionsContent = () => {
    const permissionsList = [
      {
        key: 'view_own_applications',
        label: 'View Own Applications',
        description: 'Agents can view applications they have submitted'
      },
      {
        key: 'view_application_status',
        label: 'View Application Status',
        description: 'Agents can see the status updates of their applications'
      },
      {
        key: 'upload_documents',
        label: 'Upload Documents',
        description: 'Agents can upload additional documents for their applications'
      },
      {
        key: 'edit_applications',
        label: 'Edit Applications',
        description: 'Agents can modify submitted applications (before processing)'
      },
      {
        key: 'view_communications',
        label: 'View Communications',
        description: 'Agents can see communication history (emails, SMS sent)'
      },
      {
        key: 'view_documents',
        label: 'View Documents',
        description: 'Agents can view documents attached to their applications'
      }
    ];

    const notificationSettings = [
      {
        key: 'notify_on_status_change',
        label: 'Notify on Status Change',
        description: 'Send email to agents when their application status changes (e.g., Draft → In Progress → Accepted)'
      },
      {
        key: 'notify_on_desk_shift',
        label: 'Notify on Desk Shift',
        description: 'Send email to agents when their application moves to a new processing desk'
      }
    ];

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Agent Permissions</h2>
          <p className="text-muted-foreground">Configure what external agents can access and do in the Agent Portal</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Portal Access Permissions
            </CardTitle>
            <CardDescription>
              These settings apply to all agents in your organization. Changes take effect immediately.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {agentPermissionsLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-6">
                {permissionsList.map((perm) => (
                  <div key={perm.key} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="space-y-1">
                      <Label className="text-base font-medium">{perm.label}</Label>
                      <p className="text-sm text-muted-foreground">{perm.description}</p>
                    </div>
                    <Switch
                      checked={agentPermissions[perm.key] || false}
                      onCheckedChange={(checked) => setAgentPermissions(prev => ({
                        ...prev,
                        [perm.key]: checked
                      }))}
                      data-testid={`agent-perm-${perm.key}`}
                    />
                  </div>
                ))}
                
                <Separator />
                
                <div className="flex justify-end">
                  <Button onClick={saveAgentPermissions} disabled={savingAgentPermissions}>
                    {savingAgentPermissions && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Permissions
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email Notifications Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-500" />
              Agent Email Notifications
            </CardTitle>
            <CardDescription>
              Configure when agents receive email updates about their submitted applications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {notificationSettings.map((setting) => (
                <div key={setting.key} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="space-y-1">
                    <Label className="text-base font-medium">{setting.label}</Label>
                    <p className="text-sm text-muted-foreground">{setting.description}</p>
                  </div>
                  <Switch
                    checked={agentPermissions[setting.key] || false}
                    onCheckedChange={(checked) => setAgentPermissions(prev => ({
                      ...prev,
                      [setting.key]: checked
                    }))}
                    data-testid={`agent-notif-${setting.key}`}
                  />
                </div>
              ))}
              
              <div className="mt-4 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  <strong>Note:</strong> Email notifications require SMTP to be configured in Settings → SMTP Settings. 
                  Notifications include status changes (Draft → In Progress → Accepted/Rejected) and desk movements.
                </p>
              </div>
              
              <div className="flex justify-end">
                <Button onClick={saveAgentPermissions} disabled={savingAgentPermissions}>
                  {savingAgentPermissions && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Notification Settings
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Permission Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside">
              <li><strong>View Own Applications</strong> is recommended to always be enabled so agents can track their submissions</li>
              <li>Enabling <strong>Edit Applications</strong> allows agents to modify applications only before they are processed by your team</li>
              <li><strong>Upload Documents</strong> lets agents add missing documents without contacting your team</li>
              <li><strong>Email Notifications</strong> keep agents informed about their application progress without needing to log in</li>
              <li>Changes apply to all agents immediately - no need to notify them</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render content based on active tab
  const renderContent = () => {
    if (activeTab === 'required_docs') return renderRequiredDocsContent();
    if (activeTab === 'eligibility_criteria') return renderEligibilityContent();
    if (activeTab === 'automation') return renderAutomationContent();
    if (activeTab === 'data_backup') return renderDataBackupContent();
    if (activeTab === 'academic_hierarchy') return renderAcademicHierarchyContent();
    if (activeTab === 'agent_permissions') return renderAgentPermissionsContent();
    if (activeTab === 'quick_courses') return <QuickCoursesManager api={api} />;
    if (activeTab === 'company_info') return <PublicSiteSettingsPanel />;
    return renderMasterDataContent();
  };

  return (
    <div className="flex h-[calc(100vh-120px)]" data-testid="core-settings-page">
      {/* Sidebar Navigation */}
      <div className="w-64 shrink-0 border-r bg-muted/30">
        <div className="p-4 border-b">
          <div className="flex items-center gap-2 mb-3">
            <Settings2 className="w-5 h-5 text-primary" />
            <h2 className="font-semibold">Core Settings</h2>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search settings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 bg-background"
              data-testid="settings-search"
            />
          </div>
        </div>
        
        <ScrollArea className="h-[calc(100%-85px)]">
          <div className="p-3 space-y-6">
            {filteredCategories.map((category) => (
              <div key={category.id}>
                <div className="flex items-center gap-2 px-3 mb-2">
                  <category.icon className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {category.label}
                  </span>
                </div>
                <div className="space-y-1">
                  {category.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.key;
                    return (
                      <button
                        key={item.key}
                        onClick={() => { setActiveTab(item.key); setSelectedItems([]); setTableSearch(''); }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                          isActive 
                            ? 'bg-primary text-primary-foreground shadow-sm' 
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                        data-testid={`settings-nav-${item.key}`}
                      >
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="text-sm font-medium truncate">{item.label}</span>
                        {isActive && <ChevronRight className="w-4 h-4 ml-auto shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {filteredCategories.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No settings found</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {renderContent()}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className={activeTab === 'program' ? 'sm:max-w-[550px]' : ''}>
          <DialogHeader>
            <DialogTitle>Add {activeConfig?.label?.slice(0, -1)}</DialogTitle>
            <DialogDescription>Create a new {activeConfig?.label?.toLowerCase()?.slice(0, -1)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Name *</Label>
                {activeTab === 'program' && formData.name && formData.parent_id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-[11px] gap-1"
                    disabled={aiEnriching}
                    onClick={async () => {
                      try {
                        setAiEnriching(true);
                        const uniName = parentOptions.find(p => p.id === formData.parent_id)?.name || '';
                        const res = await api().post('/master-data/programs/ai-enrich', {
                          program_name: formData.name,
                          university_name: uniName,
                        });
                        const d = res.data || {};
                        if (!d.success) {
                          toast.error('AI enrichment failed: ' + (d.error || 'unknown'));
                          return;
                        }

                        // Find-or-create Subject + Faculty master_data records under this university
                        // so the dropdowns reflect the AI suggestion (not just metadata names).
                        const findOrCreate = async (type, name) => {
                          if (!name) return null;
                          try {
                            const list = await api().get(`/master-data?type=${type}`);
                            const existing = (list.data || []).find(
                              x => x.parent_id === formData.parent_id &&
                                   (x.name || '').trim().toLowerCase() === name.trim().toLowerCase()
                            );
                            if (existing) return existing.id;
                            const created = await api().post('/master-data', {
                              type, name: name.trim(), parent_id: formData.parent_id, metadata: { auto_created_by: 'ai_enrich' }
                            });
                            return created.data?.id || null;
                          } catch (err) {
                            console.error(`findOrCreate(${type}) failed`, err);
                            return null;
                          }
                        };

                        const [subjectId, facultyId] = await Promise.all([
                          findOrCreate('subject', d.subject),
                          findOrCreate('faculty', d.faculty),
                        ]);

                        // Refresh dialog dropdowns so the newly-created records show up
                        await fetchProgramDialogOptions(formData.parent_id);

                        setFormData((prev) => ({
                          ...prev,
                          metadata: {
                            ...prev.metadata,
                            subject: subjectId || prev.metadata?.subject,
                            subject_name: d.subject || prev.metadata?.subject_name,
                            faculty: facultyId || prev.metadata?.faculty,
                            faculty_name: d.faculty || prev.metadata?.faculty_name,
                            duration: d.duration || prev.metadata?.duration,
                            level: d.level || prev.metadata?.level,
                            awarding_body: d.awarding_body || prev.metadata?.awarding_body,
                            ai_confidence: d.confidence,
                          },
                        }));
                        toast.success(`AI filled Subject "${d.subject}" + Faculty "${d.faculty}" (${Math.round((d.confidence || 0) * 100)}% confidence).`);
                      } catch (e) {
                        toast.error('AI enrichment failed: ' + (e?.response?.data?.detail || e.message));
                      } finally {
                        setAiEnriching(false);
                      }
                    }}
                    data-testid="program-ai-enrich-btn"
                  >
                    <Sparkles className="w-3 h-3" />
                    {aiEnriching ? 'Suggesting…' : 'AI Suggest'}
                  </Button>
                )}
              </div>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={`Enter ${activeConfig?.label?.toLowerCase()?.slice(0, -1)} name`}
                data-testid="create-item-name"
              />
            </div>
            {needsParent && activeTab !== 'program' && (
              <div className="space-y-2">
                <Label>{activeTab === 'city' ? 'Country' : 'University'} *</Label>
                <SearchableSelect
                  value={formData.parent_id}
                  onValueChange={(v) => {
                    setFormData({ ...formData, parent_id: v, metadata: {} });
                  }}
                  options={parentOptions.map((opt) => ({ value: opt.id, label: opt.name }))}
                  placeholder={`Select ${activeTab === 'city' ? 'country' : 'university'}`}
                  searchPlaceholder={`Search ${activeTab === 'city' ? 'countries' : 'universities'}…`}
                />
              </div>
            )}
            {/* Programs need a 2-step cascade: University -> Degree Type. The
                backend stores parent_id = degree_type_id (so programs appear in
                the Application Detail Admissions cascade). The university id
                is preserved in metadata for reference and AI enrichment. */}
            {activeTab === 'program' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>University *</Label>
                  <SearchableSelect
                    value={formData.metadata?.university_id || ''}
                    onValueChange={(v) => {
                      const uniName = parentOptions.find(o => o.id === v)?.name || '';
                      // Reset parent_id (degree type) when university changes
                      setFormData({
                        ...formData,
                        parent_id: '',
                        metadata: { university_id: v, university_name: uniName },
                      });
                      fetchProgramDialogOptions(v);
                    }}
                    options={parentOptions.map((opt) => ({ value: opt.id, label: opt.name }))}
                    placeholder="Select university"
                    searchPlaceholder="Search universities…"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Degree Type *</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => { setQuickAddType('degree_type'); setQuickAddName(''); setQuickAddOpen(true); }}
                      disabled={!formData.metadata?.university_id}
                      data-testid="program-add-degree-type-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                  </div>
                  <SearchableSelect
                    value={formData.parent_id}
                    onValueChange={(v) => {
                      const dtName = programDialogDegreeTypes.find(d => d.id === v)?.name || '';
                      setFormData(prev => ({
                        ...prev,
                        parent_id: v,
                        metadata: { ...prev.metadata, degree_type_name: dtName },
                      }));
                    }}
                    options={programDialogDegreeTypes.map(d => ({ value: d.id, label: d.name }))}
                    placeholder={formData.metadata?.university_id ? 'Select degree type' : 'Pick a university first'}
                    searchPlaceholder="Search degree types…"
                    emptyText="No degree types yet. Click Add."
                  />
                </div>
              </div>
            )}
            {/* Additional fields for Programs — show after a University is picked */}
            {activeTab === 'program' && formData.metadata?.university_id && (
              <>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Campus <Badge variant="outline" className="text-xs ml-1">Optional · Multi</Badge></Label>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2 text-xs"
                      onClick={() => { setQuickAddType('campus'); setQuickAddName(''); setQuickAddOpen(true); }}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add New
                    </Button>
                  </div>
                  <MultiSearchableSelect
                    value={Array.isArray(formData.metadata?.campuses) ? formData.metadata.campuses : (formData.metadata?.campus ? [formData.metadata.campus] : [])}
                    onValueChange={(arr) => {
                      const names = programDialogCampuses.filter(c => arr.includes(c.id)).map(c => c.name);
                      setFormData({
                        ...formData,
                        metadata: {
                          ...formData.metadata,
                          campuses: arr,
                          campus_names: names,
                          // keep legacy single fields populated with the first selection for back-compat
                          campus: arr[0] || '',
                          campus_name: names[0] || '',
                        },
                      });
                    }}
                    options={programDialogCampuses.map(c => ({ value: c.id, label: c.name }))}
                    placeholder="Select campuses"
                    searchPlaceholder="Search campuses…"
                    emptyText="No campuses found. Click Add New."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Subject <Badge variant="outline" className="text-xs ml-1">Optional</Badge></Label>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-xs"
                        onClick={() => { setQuickAddType('subject'); setQuickAddName(''); setQuickAddOpen(true); }}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                    </div>
                    <SearchableSelect
                      value={formData.metadata?.subject || ''}
                      onValueChange={(v) => {
                        const selected = programDialogSubjects.find(s => s.id === v);
                        setFormData({ ...formData, metadata: { ...formData.metadata, subject: v, subject_name: selected?.name } });
                      }}
                      options={programDialogSubjects.map(s => ({ value: s.id, label: s.name }))}
                      placeholder="Select subject"
                      searchPlaceholder="Search subjects…"
                      emptyText="No subjects. Click Add."
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Faculty <Badge variant="outline" className="text-xs ml-1">Optional</Badge></Label>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-xs"
                        onClick={() => { setQuickAddType('faculty'); setQuickAddName(''); setQuickAddOpen(true); }}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                    </div>
                    <SearchableSelect
                      value={formData.metadata?.faculty || ''}
                      onValueChange={(v) => {
                        const selected = programDialogFaculties.find(f => f.id === v);
                        setFormData({ ...formData, metadata: { ...formData.metadata, faculty: v, faculty_name: selected?.name } });
                      }}
                      options={programDialogFaculties.map(f => ({ value: f.id, label: f.name }))}
                      placeholder="Select faculty"
                      searchPlaceholder="Search faculties…"
                      emptyText="No faculties. Click Add."
                    />
                  </div>
                </div>
              </>
            )}
            {activeTab === 'program' && !formData.metadata?.university_id && (
              <div className="text-sm text-muted-foreground text-center py-4 border-2 border-dashed rounded-lg">
                Select a university first to configure Degree Type, Campus, Subject, and Faculty
              </div>
            )}

            {/* Optional logo upload for universities — used by the Form Creator
                to brand public application forms with the partner uni's logo. */}
            {activeTab === 'university' && (
              <div className="space-y-2">
                <Label>Logo <Badge variant="outline" className="text-xs ml-1">Optional</Badge></Label>
                {(() => {
                  const existing = formData.metadata?.logo_url;
                  const previewSrc = logoPreviewBlob
                    || (existing && (existing.startsWith('http') ? existing : `${process.env.REACT_APP_BACKEND_URL}${existing}`))
                    || null;
                  return (
                    <div className="flex items-center gap-3">
                      <div className="w-20 h-20 rounded-lg border bg-muted/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {previewSrc ? (
                          <img
                            src={previewSrc}
                            alt="Logo preview"
                            className="w-full h-full object-contain p-1 bg-white"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <Building2 className="w-7 h-7 text-muted-foreground/50" />
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <input
                          id="uni-logo-file-create"
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            if (f.size > 2 * 1024 * 1024) {
                              toast.error('Image must be 2 MB or smaller');
                              return;
                            }
                            setPendingLogoFile(f);
                            setLogoPreviewBlob(URL.createObjectURL(f));
                          }}
                          data-testid="university-logo-file-input"
                        />
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById('uni-logo-file-create')?.click()}
                          >
                            <Upload className="w-3.5 h-3.5 mr-1.5" />
                            {previewSrc ? 'Replace logo' : 'Browse & upload'}
                          </Button>
                          {previewSrc && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setPendingLogoFile(null);
                                setLogoPreviewBlob(null);
                                setFormData(prev => ({ ...prev, metadata: { ...prev.metadata, logo_url: '' } }));
                              }}
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          PNG, JPG, SVG or WebP · 2 MB max · uploaded after you click <strong>Create</strong>.
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="create-item-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className={activeTab === 'program' ? 'sm:max-w-[550px]' : ''}>
          <DialogHeader>
            <DialogTitle>Edit {activeConfig?.label?.slice(0, -1)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="edit-item-name"
              />
            </div>
            {needsParent && (
              <div className="space-y-2">
                <Label>{activeTab === 'city' ? 'Country' : 'University'}</Label>
                <Select value={formData.parent_id} onValueChange={(v) => setFormData({ ...formData, parent_id: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {parentOptions.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Additional fields for Programs */}
            {activeTab === 'program' && (
              <>
                <div className="space-y-2">
                  <Label>Campus <Badge variant="outline" className="text-xs ml-1">Optional</Badge></Label>
                  <Input
                    value={formData.metadata?.campus || ''}
                    onChange={(e) => setFormData({ ...formData, metadata: { ...formData.metadata, campus: e.target.value } })}
                    placeholder="e.g., London Campus, Main Campus"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Subject <Badge variant="outline" className="text-xs ml-1">Optional</Badge></Label>
                    <Input
                      value={formData.metadata?.subject || ''}
                      onChange={(e) => setFormData({ ...formData, metadata: { ...formData.metadata, subject: e.target.value } })}
                      placeholder="e.g., Computer Science"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Faculty <Badge variant="outline" className="text-xs ml-1">Optional</Badge></Label>
                    <Input
                      value={formData.metadata?.faculty || ''}
                      onChange={(e) => setFormData({ ...formData, metadata: { ...formData.metadata, faculty: e.target.value } })}
                      placeholder="e.g., Faculty of Engineering"
                    />
                  </div>
                </div>

                {/* Hero banner — shown on the public Program Detail page */}
                <div className="space-y-2 pt-2 border-t">
                  <Label>Hero Banner <Badge variant="outline" className="text-xs ml-1">Public</Badge></Label>
                  {(() => {
                    const src = formData.metadata?.hero_image || null;
                    return (
                      <div className="space-y-2">
                        <div className="w-full h-32 rounded-md border bg-muted/30 overflow-hidden flex items-center justify-center">
                          {src ? (
                            <img src={src} alt="Hero preview" className="w-full h-full object-cover" data-testid="program-hero-preview" />
                          ) : (
                            <span className="text-xs text-muted-foreground">No banner yet — recommended 1600×500 px</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            id="program-hero-file"
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (!f) return;
                              if (f.size > 2 * 1024 * 1024) {
                                toast.error('Image must be 2 MB or smaller');
                                return;
                              }
                              const reader = new FileReader();
                              reader.onload = () => {
                                setFormData((prev) => ({
                                  ...prev,
                                  metadata: { ...prev.metadata, hero_image: reader.result },
                                }));
                              };
                              reader.readAsDataURL(f);
                            }}
                            data-testid="program-hero-file-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById('program-hero-file')?.click()}
                            data-testid="program-hero-upload-btn"
                          >
                            <Upload className="w-3.5 h-3.5 mr-1.5" />
                            {src ? 'Replace banner' : 'Upload banner'}
                          </Button>
                          {src && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setFormData((prev) => ({ ...prev, metadata: { ...prev.metadata, hero_image: '' } }))}
                              data-testid="program-hero-clear-btn"
                            >
                              Remove
                            </Button>
                          )}
                          <p className="text-[11px] text-muted-foreground ml-auto">
                            PNG / JPG / WebP · max 2 MB
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </>
            )}

            {/* Optional logo upload for universities (Edit) */}
            {activeTab === 'university' && (
              <div className="space-y-2">
                <Label>Logo <Badge variant="outline" className="text-xs ml-1">Optional</Badge></Label>
                {(() => {
                  const existing = formData.metadata?.logo_url;
                  const previewSrc = logoPreviewBlob
                    || (existing && (existing.startsWith('http') ? existing : `${process.env.REACT_APP_BACKEND_URL}${existing}`))
                    || null;
                  return (
                    <div className="flex items-center gap-3">
                      <div className="w-20 h-20 rounded-lg border bg-muted/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {previewSrc ? (
                          <img
                            src={previewSrc}
                            alt="Logo preview"
                            className="w-full h-full object-contain p-1 bg-white"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <Building2 className="w-7 h-7 text-muted-foreground/50" />
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <input
                          id="uni-logo-file-edit"
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            if (f.size > 2 * 1024 * 1024) {
                              toast.error('Image must be 2 MB or smaller');
                              return;
                            }
                            setPendingLogoFile(f);
                            setLogoPreviewBlob(URL.createObjectURL(f));
                          }}
                          data-testid="university-logo-file-edit-input"
                        />
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById('uni-logo-file-edit')?.click()}
                          >
                            <Upload className="w-3.5 h-3.5 mr-1.5" />
                            {previewSrc ? 'Replace logo' : 'Browse & upload'}
                          </Button>
                          {previewSrc && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={handleClearLogo}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          PNG, JPG, SVG or WebP · 2 MB max · uploaded after you click <strong>Save</strong>.
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate} data-testid="edit-item-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Add Dialog for Program Dialog */}
      <Dialog open={quickAddOpen} onOpenChange={setQuickAddOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add New {quickAddType.charAt(0).toUpperCase() + quickAddType.slice(1)}</DialogTitle>
            <DialogDescription>
              Quick add a new {quickAddType} for the selected university
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={quickAddName}
                onChange={(e) => setQuickAddName(e.target.value)}
                placeholder={`Enter ${quickAddType} name`}
                onKeyDown={(e) => e.key === 'Enter' && handleQuickAdd()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickAddOpen(false)}>Cancel</Button>
            <Button onClick={handleQuickAdd} disabled={quickAddLoading || !quickAddName.trim()}>
              {quickAddLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoreSettingsPage;
